import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import * as MarketingAPI from '../api/Marketing';
import { ProContact, Campaign, MktEmail, AIEmailRequest, ContactList } from '../api/Marketing';

const DEFAULT_TEST_RECIPIENTS = ['info@cectrack.com', 'support@cectrack.com', 'cectrack@gmail.com'];
const LS_KEY = 'mkt_test_recipients';
import { getAllProfessions, Profession } from '../api/Professions';

// Profession identity now lives in PROFESSIONID (canonical codes from the
// Profession collection — ARCH, PE, CPA, LAW...). This alias map exists only
// to translate legacy free-text values (old CSV columns, hand-typed RELATION
// codes) into a real professionID — it is NOT a parallel taxonomy. Contacts
// that already have PROFESSIONID never touch this.
const LEGACY_ALIAS_TO_PROFESSIONID: Record<string, string> = {
  ARC: 'ARCH', RA: 'ARCH', ARCHITECT: 'ARCH',
  ENG: 'PE', ENGINEER: 'PE',
  DDS: 'DDS', DMD: 'DDS', DENTIST: 'DDS',
  LAW: 'LAW', ATTORNEY: 'LAW',
  LA: 'LA', 'LANDSCAPE ARCHITECT': 'LA',
  CPA: 'CPA', ACCOUNTANT: 'CPA',
  LMHC: 'LMHC',
  LCSW: 'LCSW',
};

const legacyToProfessionID = (raw?: string): string | null => {
  if (!raw?.trim()) return null;
  return LEGACY_ALIAS_TO_PROFESSIONID[raw.toUpperCase().trim()] || null;
};

/** Resolves a contact's profession display code + name, preferring the
 * canonical PROFESSIONID and falling back to legacy RELATION/PROFESSION
 * text for the handful of not-yet-backfilled contacts. */
const getContactProfession = (contact: ProContact, professions: Profession[]): { code: string; name: string } => {
  const pid = contact.PROFESSIONID || legacyToProfessionID(contact.PROFESSION) || legacyToProfessionID(contact.RELATION);
  if (pid) {
    const match = professions.find(p => p.professionID === pid);
    return { code: pid, name: match?.name || pid };
  }
  const legacy = contact.PROFESSION || contact.RELATION;
  return legacy ? { code: legacy, name: legacy } : { code: 'N/A', name: 'N/A' };
};

const MarketingEmailAdmin = () => {
  usePageTitle('Marketing Email Admin');

  const [activeTab, setActiveTab] = useState<'campaigns' | 'contacts' | 'emails' | 'lists'>('campaigns');

  // Contact list state
  const [lists, setLists] = useState<ContactList[]>([]);
  const [showListForm, setShowListForm] = useState(false);
  const [editingList, setEditingList] = useState<ContactList | null>(null);
  const [listForm, setListForm] = useState({ name: '', description: '' });
  const [addToListTarget, setAddToListTarget] = useState('');
  const [expandedListId, setExpandedListId] = useState<string | null>(null);
  const [expandedListContacts, setExpandedListContacts] = useState<ProContact[]>([]);
  const [expandedListLoading, setExpandedListLoading] = useState(false);
  
  // Campaign state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [mktEmails, setMktEmails] = useState<MktEmail[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    campaignname: '',
    campaignnote: '',
    emailsubject: '',
    emailbody: '',
    emailtemplateid: undefined as number | undefined,
    campaignsentto: [] as number[]
  });

  // Contact state
  const [contacts, setContacts] = useState<ProContact[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set());
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [contactFilters, setContactFilters] = useState({
    search: '',
    state: '',
    relation: '',
    emailOptout: false,
    lists: [] as number[]
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<ProContact | null>(null);
  const [contactForm, setContactForm] = useState({
    FIRST: '',
    LAST: '',
    EMAIL: '',
    COMPANY: '',
    TITLE: '',
    STATE: '',
    PROFESSIONID: '',
    NOTE: '',
    EMAILOPTOUT: false
  });

  // AI Email Generation state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiStep, setAiStep] = useState<'form' | 'loading' | 'preview'>('form');
  const [aiLoadingMsg, setAiLoadingMsg] = useState('');
  const [aiForm, setAiForm] = useState<AIEmailRequest>({
    topic: '',
    purpose: 'newsletter',
    targetAudience: 'all',
    tone: 'professional',
    includeImage: true,
    imageStyle: 'auto',
    additionalContext: '',
    colorScheme: 'navy-teal',
    includeSampleCards: false,
  });
  const [aiResult, setAiResult] = useState<{ html: string; suggestedName: string; imageUrl: string | null } | null>(null);
  const [aiTemplateName, setAiTemplateName] = useState('');
  const [aiSaving, setAiSaving] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string | null>(null);
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const heroFileInputRef = useRef<HTMLInputElement>(null);

  // Persistent top logo
  const [topLogoUrl, setTopLogoUrl] = useState<string | null>(null);
  const [topLogoLoading, setTopLogoLoading] = useState(false);
  const [topLogoUploading, setTopLogoUploading] = useState(false);
  const topLogoFileInputRef = useRef<HTMLInputElement>(null);
  const [topLogoLink, setTopLogoLink] = useState('https://cectrack.com');
  const [heroImageLink, setHeroImageLink] = useState('https://cectrack.com');

  // Email template state
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeType, setEmailCodeType] = useState<'html' | 'json'>('html');
  const [emailTemplateName, setEmailTemplateName] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MktEmail | null>(null);

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewKey, setPreviewKey] = useState(0); // Cache buster for preview
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [showEmailHistoryModal, setShowEmailHistoryModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendingCampaignId, setSendingCampaignId] = useState<string>('');

  // Unsubscriber counts per campaign (keyed by campaign _id)
  const [unsubCounts, setUnsubCounts] = useState<Record<string, number>>({});
  const [unsubModal, setUnsubModal] = useState<{
    open: boolean;
    campaignName: string;
    contacts: any[];
    loading: boolean;
  }>({ open: false, campaignName: '', contacts: [], loading: false });

  // Campaign send-results modal (sent/failed breakdown + retry)
  const [resultsModal, setResultsModal] = useState<{
    open: boolean;
    campaignId: string;
    campaignName: string;
    data: MarketingAPI.CampaignResults | null;
    loading: boolean;
    retrying: boolean;
  }>({ open: false, campaignId: '', campaignName: '', data: null, loading: false, retrying: false });

  // Email history state
  const [emailHistory, setEmailHistory] = useState<MarketingAPI.ContactEmailHistory | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // CSV Import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateFileInputRef = useRef<HTMLInputElement>(null);
  const [csvImportStep, setCsvImportStep] = useState<1 | 2 | 3 | 4>(1);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Array<{ row: number; field: string; error: string }>>([]);
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
    defaultProfessionID: 'ARCH'
  });
  const [importResults, setImportResults] = useState({
    total: 0,
    success: 0,
    skipped: 0,
    failed: 0,
    errors: [] as Array<{ row: number; error: string }>
  });

  // Recipient selection filters and sorting
  const [recipientFilters, setRecipientFilters] = useState({
    search: '',
    state: '',
    relation: '',
    profession: '',
    lists: [] as number[]
  });
  const [recipientSortBy, setRecipientSortBy] = useState<'name' | 'email' | 'state' | 'relation'>('relation');
  const [recipientSortOrder, setRecipientSortOrder] = useState<'asc' | 'desc'>('asc');
  const [recipientViewMode, setRecipientViewMode] = useState<'flat' | 'grouped'>('grouped');

  // Test recipients config (persisted in localStorage)
  const [testRecipients, setTestRecipients] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return stored ? JSON.parse(stored) : DEFAULT_TEST_RECIPIENTS;
    } catch { return DEFAULT_TEST_RECIPIENTS; }
  });
  const [newTestRecipientInput, setNewTestRecipientInput] = useState('');
  const [showTestRecipientsPanel, setShowTestRecipientsPanel] = useState(false);
  const [sendingTestToAll, setSendingTestToAll] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendProgress, setSendProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  // Get unique states from contacts for dropdown
  const uniqueStates = useMemo(() => {
    const states = contacts
      .map(contact => contact.STATE)
      .filter(state => state && state.trim() !== '');
    return Array.from(new Set(states)).sort();
  }, [contacts]);

  // Get unique professions present in the contact list, for filter dropdowns
  const uniqueRelations = useMemo(() => {
    const uniqueMap = new Map<string, string>();
    contacts.forEach(contact => {
      const { code, name } = getContactProfession(contact, professions);
      if (code === 'N/A' || uniqueMap.has(code)) return;
      uniqueMap.set(code, name);
    });
    return Array.from(uniqueMap.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [contacts, professions]);

  useEffect(() => {
    fetchCampaigns();
    fetchContacts();
    fetchMktEmails();
    fetchProfessions();
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const data = await MarketingAPI.getAllLists();
      setLists(data || []);
    } catch (err: any) {
      console.error('Error loading lists:', err);
      setLists([]);
    }
  };

  const fetchProfessions = async () => {
    try {
      const data = await getAllProfessions();
      setProfessions(data || []);
    } catch (err: any) {
      console.error('Error loading professions:', err);
      setProfessions([]);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const data = await MarketingAPI.getAllCampaigns();
      setCampaigns(data || []);
      // Fetch unsubscriber counts for all sent campaigns
      MarketingAPI.getUnsubscriberCounts().then(setUnsubCounts).catch(() => {});
    } catch (err: any) {
      console.error('Error loading campaigns:', err);
      setCampaigns([]);
      // Don't show error if it's just empty data
      if (err.response?.status !== 404) {
        setError('Failed to load campaigns');
      }
    }
  };

  const openUnsubModal = async (campaign: Campaign) => {
    setUnsubModal({ open: true, campaignName: campaign.campaignname, contacts: [], loading: true });
    try {
      const data = await MarketingAPI.getCampaignUnsubscribers(campaign._id);
      setUnsubModal(prev => ({ ...prev, contacts: data.contacts, loading: false }));
    } catch {
      setUnsubModal(prev => ({ ...prev, loading: false }));
    }
  };

  const openResultsModal = async (campaign: Campaign) => {
    setResultsModal({ open: true, campaignId: campaign._id, campaignName: campaign.campaignname, data: null, loading: true, retrying: false });
    try {
      const data = await MarketingAPI.getCampaignResults(campaign._id);
      setResultsModal(prev => ({ ...prev, data, loading: false }));
    } catch {
      setResultsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRetryFailed = async () => {
    if (!resultsModal.campaignId) return;
    const failedCount = resultsModal.data?.current?.failed || 0;
    if (failedCount === 0) return;
    if (!window.confirm(`Resend to ${failedCount} recipient${failedCount !== 1 ? 's' : ''} whose last send attempt failed?`)) return;

    setResultsModal(prev => ({ ...prev, retrying: true }));
    try {
      const result = await MarketingAPI.retryCampaignFailed(resultsModal.campaignId);
      setSuccess(result.message);
      const data = await MarketingAPI.getCampaignResults(resultsModal.campaignId);
      setResultsModal(prev => ({ ...prev, data, retrying: false }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry sending');
      setResultsModal(prev => ({ ...prev, retrying: false }));
    }
  };

  const fetchContacts = async () => {
    try {
      const data = await MarketingAPI.getAllContacts(contactFilters);
      setContacts(data || []);
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setContacts([]);
      if (err.response?.status !== 404) {
        setError('Failed to load contacts');
      }
    }
  };

  const fetchMktEmails = async () => {
    try {
      const data = await MarketingAPI.getAllMktEmails();
      setMktEmails(data || []);
    } catch (err: any) {
      console.error('Error loading email templates:', err);
      setMktEmails([]);
      if (err.response?.status !== 404) {
        setError('Failed to load email templates');
      }
    }
  };

  // Campaign handlers
  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!campaignForm.emailbody) {
      setError('Email body is required');
      return;
    }
    
    if (selectedContacts.size === 0) {
      setError('Please select at least one recipient');
      return;
    }

    try {
      setLoading(true);
      if (editingCampaign) {
        await MarketingAPI.updateCampaign(editingCampaign._id, {
          ...campaignForm,
          campaignsentto: Array.from(selectedContacts)
        });
        setSuccess('Campaign updated successfully');
      } else {
        await MarketingAPI.createCampaign({
          ...campaignForm,
          campaignsentto: Array.from(selectedContacts)
        });
        setSuccess('Campaign created successfully');
      }
      setShowCampaignForm(false);
      setCampaignForm({ 
        campaignname: '', 
        campaignnote: '', 
        emailsubject: '', 
        emailbody: '', 
        emailtemplateid: undefined,
        campaignsentto: [] 
      });
      setSelectedContacts(new Set());
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err: any) {
      setError(editingCampaign ? 'Failed to update campaign' : 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      campaignname: campaign.campaignname,
      campaignnote: campaign.campaignnote || '',
      emailsubject: campaign.emailsubject || '',
      emailbody: campaign.emailbody || '',
      emailtemplateid: campaign.emailtemplateid,
      campaignsentto: campaign.campaignsentto
    });
    setSelectedContacts(new Set(campaign.campaignsentto));
    setShowCampaignForm(true);
  };

  const handleDuplicateCampaign = (campaign: Campaign) => {
    setEditingCampaign(null); // Set to null so it creates a new campaign
    setCampaignForm({
      campaignname: `${campaign.campaignname} (Copy)`,
      campaignnote: campaign.campaignnote || '',
      emailsubject: campaign.emailsubject || '',
      emailbody: campaign.emailbody || '',
      emailtemplateid: campaign.emailtemplateid,
      campaignsentto: campaign.campaignsentto
    });
    setSelectedContacts(new Set(campaign.campaignsentto));
    setShowCampaignForm(true);
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await MarketingAPI.deleteCampaign(id);
      setSuccess('Campaign deleted');
      fetchCampaigns();
    } catch (err: any) {
      setError('Failed to delete campaign');
    }
  };

  const handleCancelSchedule = async (id: string) => {
    if (!window.confirm('Cancel the scheduled send and revert to draft?')) return;
    
    try {
      setLoading(true);
      const campaign = campaigns.find(c => c._id === id);
      if (!campaign) return;
      
      // Update campaign to draft status and remove scheduled date
      await MarketingAPI.updateCampaign(id, {
        ...campaign,
        status: 'draft',
        scheduledDate: undefined
      });
      
      setSuccess('Scheduled send cancelled. Campaign reverted to draft.');
      fetchCampaigns();
    } catch (err: any) {
      setError('Failed to cancel schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (id: string, scheduledDate?: Date) => {
    try {
      setLoading(true);
      setShowProgress(true);
      setSendProgress(0);
      
      const progressInterval = setInterval(() => {
        setSendProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const result = await MarketingAPI.sendCampaign(id, scheduledDate);
      
      clearInterval(progressInterval);
      setSendProgress(100);
      setTimeout(() => {
        setShowProgress(false);
        setSendProgress(0);
      }, 2000);

      setSuccess(result.message);
      setShowScheduleModal(false);
      fetchCampaigns();
    } catch (err: any) {
      setError('Failed to send campaign');
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    try {
      setLoading(true);
      await MarketingAPI.sendTestEmail(sendingCampaignId, testEmail);
      setSuccess(`Test email sent to ${testEmail}`);
      setShowTestEmailModal(false);
      setTestEmail('');
    } catch (err: any) {
      setError('Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const openScheduleModal = (campaignId: string) => {
    setSendingCampaignId(campaignId);
    const now = new Date();
    setScheduleDate(now.toISOString().split('T')[0]);
    setScheduleTime(now.toTimeString().slice(0, 5));
    setShowScheduleModal(true);
  };

  const openTestEmailModal = (campaignId: string) => {
    setSendingCampaignId(campaignId);
    setShowTestEmailModal(true);
  };

  const openPreviewModal = (campaign: Campaign) => {
    setPreviewHtml(campaign.emailbody || '<p>No email body</p>');
    setShowPreviewModal(true);
  };

  const saveTestRecipients = (list: string[]) => {
    setTestRecipients(list);
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  };

  const addTestRecipient = () => {
    const email = newTestRecipientInput.trim().toLowerCase();
    if (!email || testRecipients.includes(email)) return;
    saveTestRecipients([...testRecipients, email]);
    setNewTestRecipientInput('');
  };

  const removeTestRecipient = (email: string) => {
    saveTestRecipients(testRecipients.filter(e => e !== email));
  };

  const handleSendTestToAll = async () => {
    if (testRecipients.length === 0) { setError('No test recipients configured'); return; }
    if (!previewHtml) { setError('No email content to send'); return; }
    try {
      setSendingTestToAll(true);
      const result = await MarketingAPI.sendTestEmailToAll(previewHtml, 'Email Preview', testRecipients);
      setSuccess(result.message);
    } catch (err: any) {
      setError('Failed to send test emails');
    } finally {
      setSendingTestToAll(false);
    }
  };

  const openEmailHistoryModal = async (contact: ProContact) => {
    setShowEmailHistoryModal(true);
    setLoadingHistory(true);
    try {
      const history = await MarketingAPI.getContactEmailHistory(contact.PID);
      setEmailHistory(history);
    } catch (err: any) {
      setError('Failed to load email history');
      console.error('Error loading email history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleScheduledSend = () => {
    if (!scheduleDate || !scheduleTime) {
      setError('Please select date and time');
      return;
    }
    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
    handleSendCampaign(sendingCampaignId, scheduledDateTime);
  };

  // Contact handlers
  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.EMAIL)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      if (editingContact) {
        await MarketingAPI.updateContact(editingContact._id, contactForm);
        setSuccess('Contact updated successfully');
      } else {
        await MarketingAPI.createContact(contactForm);
        setSuccess('Contact created successfully');
      }
      setShowContactForm(false);
      setContactForm({
        FIRST: '',
        LAST: '',
        EMAIL: '',
        COMPANY: '',
        TITLE: '',
        STATE: '',
        PROFESSIONID: '',
        NOTE: '',
        EMAILOPTOUT: false
      });
      setEditingContact(null);
      fetchContacts();
    } catch (err: any) {
      setError(editingContact ? 'Failed to update contact' : 'Failed to create contact');
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = (contact: ProContact) => {
    setEditingContact(contact);
    const inferredProfessionID = contact.PROFESSIONID || legacyToProfessionID(contact.PROFESSION) || legacyToProfessionID(contact.RELATION) || '';
    setContactForm({
      FIRST: contact.FIRST || '',
      LAST: contact.LAST || '',
      EMAIL: contact.EMAIL || '',
      COMPANY: contact.COMPANY || '',
      TITLE: contact.TITLE || '',
      STATE: contact.STATE || '',
      PROFESSIONID: inferredProfessionID,
      NOTE: contact.NOTE || '',
      EMAILOPTOUT: contact.EMAILOPTOUT || false
    });
    setShowContactForm(true);
  };

  const handleBulkDelete = async () => {
    const count = checkedIds.size;
    if (!window.confirm(`Delete ${count} contact${count !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const result = await MarketingAPI.bulkDeleteContacts(Array.from(checkedIds));
      setSuccess(`Deleted ${result.deletedCount} contacts`);
      setCheckedIds(new Set());
      fetchContacts();
    } catch (err: any) {
      setError('Failed to bulk delete contacts');
    }
  };

  const handleAddSelectedToList = async () => {
    if (!addToListTarget || checkedIds.size === 0) return;
    const pids = contacts.filter(c => checkedIds.has(c._id)).map(c => c.PID);
    try {
      const result = await MarketingAPI.addContactsToList(addToListTarget, pids);
      setSuccess(`Added ${result.matchedCount} contact${result.matchedCount !== 1 ? 's' : ''} to list`);
      setCheckedIds(new Set());
      setAddToListTarget('');
      fetchContacts();
      fetchLists();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add contacts to list');
    }
  };

  const handleListFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listForm.name.trim()) return;
    try {
      if (editingList) {
        await MarketingAPI.updateList(editingList._id, listForm);
        setSuccess('List updated');
      } else {
        await MarketingAPI.createList(listForm);
        setSuccess('List created');
      }
      setShowListForm(false);
      setEditingList(null);
      setListForm({ name: '', description: '' });
      fetchLists();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save list');
    }
  };

  const handleDeleteList = async (list: ContactList) => {
    if (!window.confirm(`Delete list "${list.name}"? Contacts stay intact — only the grouping is removed.`)) return;
    try {
      await MarketingAPI.deleteList(list._id);
      setSuccess('List deleted');
      fetchLists();
      fetchContacts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete list');
    }
  };

  const handleToggleListMembers = async (list: ContactList) => {
    if (expandedListId === list._id) {
      setExpandedListId(null);
      return;
    }
    setExpandedListId(list._id);
    setExpandedListLoading(true);
    try {
      const data = await MarketingAPI.getAllContacts({ lists: [list.listID] });
      setExpandedListContacts(data || []);
    } catch (err: any) {
      console.error('Error loading list members:', err);
      setExpandedListContacts([]);
    } finally {
      setExpandedListLoading(false);
    }
  };

  const toggleChecked = (id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleCheckAll = () => {
    if (checkedIds.size === contacts.length && contacts.length > 0) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(contacts.map(c => c._id)));
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await MarketingAPI.deleteContact(id);
      setSuccess('Contact deleted');
      fetchContacts();
    } catch (err: any) {
      setError('Failed to delete contact');
    }
  };

  // CSV Import handlers

  // Handles quoted fields containing commas (e.g. "Dentist, General Practice")
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'FIRST', 'LAST', 'EMAIL', 'COMPANY', 'TITLE', 'STATE', 'PROFESSIONID', 'NOTE', 'TEL_BUS',
      'STREET', 'CITY', 'ZIP', 'WEB',
      'NPI', 'CREDENTIAL', 'GENDER', 'SPECIALTY', 'LICENSE_NUMBER', 'LICENSE_STATE',
      'GOOGLE_RATING', 'GOOGLE_REVIEW_COUNT', 'GOOGLE_ADDRESS', 'PLACE_ID'
    ];
    const sampleData = [
      'John,Doe,john.doe@example.com,Chelsea Dental Care,DDS,NY,DDS,,212-555-1234,135 W 27th St,New York,10001,https://example.com,1659677508,DDS,,Dentist,049431,NY,4.5,120,"135 W 27th St, New York NY 10001",ChIJeeeEfKVZwokR',
    ];
    
    const csvContent = [headers.join(','), ...sampleData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contact_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCSVFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      setCsvFile(file);
      
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = parseCSVLine(lines[0]);

      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setCsvHeaders(headers);
      setCsvData(data);
      
      // Auto-map common field names
      const autoMapping: Record<string, string> = {};
      const fieldMap: Record<string, string[]> = {
        'FIRST':               ['first', 'firstname', 'first_name', 'given_name'],
        'LAST':                ['last', 'lastname', 'last_name', 'surname', 'family_name'],
        'EMAIL':               ['email', 'e-mail', 'email_address', 'mail'],
        'COMPANY':             ['company', 'organization', 'org', 'business', 'place_name'],
        'TITLE':               ['title', 'job_title', 'position', 'role'],
        'STATE':               ['state', 'st', 'province', 'region'],
        'PROFESSIONID':        ['professionid', 'profession_id', 'professioncode', 'profession_code'],
        'RELATION':            ['relation', 'relation_code', 'code', 'category_code'],
        'PROFESSION':          ['profession', 'industry', 'occupation', 'job_type'],
        'NOTE':                ['note', 'notes', 'memo', 'comment', 'comments'],
        'TEL_BUS':             ['phone', 'tel', 'telephone', 'tel_bus', 'business_phone', 'work_phone'],
        'STREET':              ['street', 'address', 'addr', 'street_address'],
        'CITY':                ['city', 'town', 'municipality'],
        'ZIP':                 ['zip', 'zipcode', 'zip_code', 'postal', 'postal_code'],
        'WEB':                 ['web', 'website', 'url', 'webpage'],
        'NPI':                 ['npi', 'national_provider_id', 'national_provider_identifier'],
        'CREDENTIAL':          ['credential', 'credentials', 'degree', 'cred'],
        'GENDER':              ['gender', 'sex'],
        'SPECIALTY':           ['specialty', 'specialization', 'dental_specialty', 'medical_specialty'],
        'LICENSE_NUMBER':      ['license_number', 'license', 'lic_number', 'license_no'],
        'LICENSE_STATE':       ['license_state', 'lic_state', 'issuing_state'],
        'GOOGLE_RATING':       ['google_rating', 'rating', 'stars', 'google_stars'],
        'GOOGLE_REVIEW_COUNT': ['google_review_count', 'review_count', 'reviews', 'num_reviews'],
        'GOOGLE_ADDRESS':      ['google_address', 'formatted_address', 'full_address'],
        'PLACE_ID':            ['place_id', 'google_place_id', 'placeid'],
      };
      
      headers.forEach(csvHeader => {
        const lowerHeader = csvHeader.toLowerCase();
        for (const [dbField, variations] of Object.entries(fieldMap)) {
          if (variations.includes(lowerHeader) || lowerHeader === dbField.toLowerCase()) {
            autoMapping[csvHeader] = dbField;
            break;
          }
        }
      });
      
      setFieldMapping(autoMapping);
      setCsvImportStep(2);
      
    } catch (err: any) {
      setError('Failed to read CSV file. Please check the format.');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const validateCSVData = () => {
    const errors: Array<{ row: number; field: string; error: string }> = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const existingEmails = new Set(contacts.map(c => c.EMAIL?.toLowerCase()));
    const csvEmails = new Set<string>();
    
    csvData.forEach((row, index) => {
      const mappedRow: any = {};
      Object.keys(fieldMapping).forEach(csvField => {
        const dbField = fieldMapping[csvField];
        mappedRow[dbField] = row[csvField];
      });
      
      // ONLY VALIDATE EMAIL - Email is the only required field
      if (!mappedRow.EMAIL) {
        errors.push({ row: index + 1, field: 'EMAIL', error: 'Email is required' });
      } else {
        // Validate email format
        if (!emailRegex.test(mappedRow.EMAIL)) {
          errors.push({ row: index + 1, field: 'EMAIL', error: 'Invalid email format' });
        }
        
        // Check for duplicates within CSV
        const emailLower = mappedRow.EMAIL.toLowerCase();
        if (csvEmails.has(emailLower)) {
          errors.push({ row: index + 1, field: 'EMAIL', error: 'Duplicate email in CSV' });
        } else {
          csvEmails.add(emailLower);
        }
        
        // Check against existing contacts (only warn if not updating)
        if (existingEmails.has(emailLower) && !importOptions.updateExisting) {
          errors.push({ row: index + 1, field: 'EMAIL', error: 'Email already exists (enable "Update existing" to overwrite)' });
        }
      }
      
      // Optional validation: State should be 2 letters IF provided
      if (mappedRow.STATE && mappedRow.STATE.length !== 2) {
        errors.push({ row: index + 1, field: 'STATE', error: 'State should be 2 letters (e.g., CA, NY)' });
      }
    });
    
    setValidationErrors(errors);
    setCsvImportStep(3);
  };

  const executeCSVImport = async () => {
    try {
      setLoading(true);
      const results = {
        total: csvData.length,
        success: 0,
        skipped: 0,
        failed: 0,
        errors: [] as Array<{ row: number; error: string }>
      };
      
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];
        const mappedContact: any = { EMAILOPTOUT: false };
        
        // Map fields
        Object.keys(fieldMapping).forEach(csvField => {
          const dbField = fieldMapping[csvField];
          if (row[csvField]) {
            mappedContact[dbField] = row[csvField];
          }
        });
        
        // Resolve PROFESSIONID (the canonical field): an explicitly-mapped
        // CSV column wins, then infer from legacy RELATION/PROFESSION text
        // via the same alias map used for existing contacts, then fall back
        // to the configured default. RELATION/PROFESSION, if mapped, are
        // still stored as-is for historical reference but no longer drive
        // profession identity.
        if (!mappedContact.PROFESSIONID) {
          mappedContact.PROFESSIONID =
            legacyToProfessionID(mappedContact.PROFESSION) ||
            legacyToProfessionID(mappedContact.RELATION) ||
            importOptions.defaultProfessionID;
        }

        // Trim ZIP to first 5 digits (handles 9-digit ZIP+4 format)
        if (mappedContact.ZIP && mappedContact.ZIP.length > 5) {
          mappedContact.ZIP = mappedContact.ZIP.replace(/\D/g, '').slice(0, 5);
        }

        // Check if has validation errors for this row
        const rowErrors = validationErrors.filter(e => e.row === i + 1);
        if (rowErrors.length > 0) {
          results.failed++;
          results.errors.push({ row: i + 1, error: rowErrors.map(e => e.error).join(', ') });
          continue;
        }
        
        try {
          // Check for existing contact
          const existingContact = contacts.find(
            c => c.EMAIL?.toLowerCase() === mappedContact.EMAIL.toLowerCase()
          );
          
          if (existingContact) {
            if (importOptions.updateExisting) {
              await MarketingAPI.updateContact(existingContact._id, mappedContact);
              results.success++;
            } else if (importOptions.skipDuplicates) {
              results.skipped++;
            }
          } else {
            await MarketingAPI.createContact(mappedContact);
            results.success++;
          }
        } catch (err: any) {
          results.failed++;
          results.errors.push({ row: i + 1, error: err.message || 'Import failed' });
        }
      }
      
      setImportResults(results);
      setCsvImportStep(4);
      fetchContacts();
      
    } catch (err: any) {
      setError('Failed to import contacts');
    } finally {
      setLoading(false);
    }
  };

  const resetCSVImport = () => {
    setCsvImportStep(1);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setValidationErrors([]);
    setImportResults({ total: 0, success: 0, skipped: 0, failed: 0, errors: [] });
    setShowCSVImportModal(false);
  };

  const handleCSVImport = handleCSVFileSelect; // Keep for backward compatibility

  // Email template handlers
  const handleSaveEmailTemplate = async () => {
    if (!emailTemplateName || !emailCode) {
      setError('Template name and code are required');
      return;
    }

    try {
      setLoading(true);
      if (editingTemplate) {
        await MarketingAPI.updateMktEmail(editingTemplate._id, {
          mktemailname: emailTemplateName,
          mktemailtype: emailCodeType,
          mktemailnote: `Updated from admin panel - ${emailCodeType}`,
          mktemailpage: emailCode,
          html: emailCode // Save HTML content
        });
        setSuccess('Email template updated');
      } else {
        await MarketingAPI.createMktEmail({
          mktemailname: emailTemplateName,
          mktemailtype: emailCodeType,
          mktemailnote: `Created from admin panel - ${emailCodeType}`,
          mktemailpage: emailCode,
          html: emailCode // Save HTML content
        });
        setSuccess('Email template saved');
      }
      setEmailTemplateName('');
      setEmailCode('');
      setShowEmailForm(false);
      setEditingTemplate(null);
      fetchMktEmails();
    } catch (err: any) {
      setError(editingTemplate ? 'Failed to update email template' : 'Failed to save email template');
    } finally {
      setLoading(false);
    }
  };

  const handleHeroFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroImageFile(file);
    setHeroImagePreview(URL.createObjectURL(file));
    setHeroImageUrl(null);
    try {
      setHeroImageUploading(true);
      const result = await MarketingAPI.uploadHeroImage(file);
      setHeroImageUrl(result.imageUrl);
    } catch (err: any) {
      setError('Failed to upload hero image');
      setHeroImageFile(null);
      setHeroImagePreview(null);
    } finally {
      setHeroImageUploading(false);
    }
  };

  const clearHeroImage = () => {
    setHeroImageFile(null);
    setHeroImagePreview(null);
    setHeroImageUrl(null);
    if (heroFileInputRef.current) heroFileInputRef.current.value = '';
  };

  const loadTopLogo = async () => {
    try {
      setTopLogoLoading(true);
      const { imageUrl } = await MarketingAPI.getTopLogoImage();
      setTopLogoUrl(imageUrl);
    } catch (_) {} finally {
      setTopLogoLoading(false);
    }
  };

  const handleTopLogoFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setTopLogoUploading(true);
      const { imageUrl } = await MarketingAPI.uploadTopLogoImage(file);
      setTopLogoUrl(imageUrl);
      setSuccess('Top logo saved to S3.');
    } catch (err: any) {
      setError('Failed to upload top logo');
    } finally {
      setTopLogoUploading(false);
      if (topLogoFileInputRef.current) topLogoFileInputRef.current.value = '';
    }
  };

  const handleDeleteTopLogo = async () => {
    if (!window.confirm('Remove the persistent top logo?')) return;
    try {
      await MarketingAPI.deleteTopLogoImage();
      setTopLogoUrl(null);
      setSuccess('Top logo removed.');
    } catch (_) {
      setError('Failed to delete top logo');
    }
  };

  // ── AI Email Generation ────────────────────────────────────────────────────
  const handleGenerateAI = async () => {
    if (!aiForm.topic.trim()) { setError('Please enter a topic or subject.'); return; }
    setAiStep('loading');
    const hasUpload = !!heroImageUrl;
    setAiLoadingMsg('Generating email content via GPT-4…');
    try {
      if (aiForm.includeImage && !hasUpload) setAiLoadingMsg('Generating email content… then creating hero image (~10 s)…');
      const result = await MarketingAPI.generateEmailWithAI({
        ...aiForm,
        uploadedImageUrl: heroImageUrl || undefined,
        topLogoLink,
        heroImageLink,
      });
      setAiResult(result);
      setAiTemplateName(result.suggestedName);
      setAiStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'AI generation failed. Please try again.');
      setAiStep('form');
    }
  };

  const handleSaveAITemplate = async () => {
    if (!aiResult) return;
    if (!aiTemplateName.trim()) { setError('Please enter a template name.'); return; }
    setAiSaving(true);
    try {
      await MarketingAPI.createMktEmail({
        mktemailname: aiTemplateName,
        mktemailtype: 'html',
        mktemailnote: `AI-generated — ${aiForm.purpose} — ${aiForm.targetAudience}`,
        mktemailpage: aiResult.html,
        html: aiResult.html,
      });
      setSuccess(`Template "${aiTemplateName}" saved successfully.`);
      setShowAIModal(false);
      setAiStep('form');
      setAiResult(null);
      setAiForm({ topic: '', purpose: 'newsletter', targetAudience: 'all', tone: 'professional', includeImage: true, imageStyle: 'auto', additionalContext: '', colorScheme: 'navy-teal', includeSampleCards: false });
      clearHeroImage();
      fetchMktEmails();
    } catch {
      setError('Failed to save template.');
    } finally {
      setAiSaving(false);
    }
  };

  const handleEditTemplate = (template: MktEmail) => {
    setEditingTemplate(template);
    setEmailTemplateName(template.mktemailname);
    setEmailCodeType((template.mktemailtype as 'html' | 'json') || 'html');
    setEmailCode(template.html || template.mktemailpage || '');
    setShowEmailForm(true);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) return;
    
    try {
      setLoading(true);
      await MarketingAPI.deleteMktEmail(id);
      setSuccess('Email template deleted');
      fetchMktEmails();
    } catch (err: any) {
      setError('Failed to delete email template');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTemplate = async (template: MktEmail) => {
    if (template.mktemailtype === 'json') {
      setError('JSON preview not supported yet.Use edit to view the code.');
      return;
    }
    
    // Increment preview key to force re-render and clear cache
    setPreviewKey(prev => prev + 1);
    
    // Fetch fresh template from database to avoid cache issues
    try {
      const response = await fetch(`/api/marketing-emails/${template.mktemailid}`);
      if (response.ok) {
        const freshTemplate = await response.json();
        setPreviewHtml(freshTemplate.mktemailpage || '<p>No template code available</p>');
      } else {
        // Fallback to cached version if fetch fails
        setPreviewHtml(template.mktemailpage || '<p>No template code available</p>');
      }
    } catch (error) {
      console.error('Error fetching fresh template:', error);
      // Fallback to cached version
      setPreviewHtml(template.mktemailpage || '<p>No template code available</p>');
    }
    
    setShowPreviewModal(true);
  };

  const handleTemplateFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    
    if (fileExtension !== 'html' && fileExtension !== 'json') {
      setError('Please select an HTML or JSON file');
      if (templateFileInputRef.current) {
        templateFileInputRef.current.value = '';
      }
      return;
    }

    try {
      setLoading(true);
      const text = await file.text();
      
      // Auto-detect and set file type
      if (fileExtension === 'html') {
        setEmailCodeType('html');
      } else if (fileExtension === 'json') {
        setEmailCodeType('json');
      }
      
      // Load content into editor
      setEmailCode(text);
      
      // Auto-populate template name from filename (without extension) if not already set
      if (!emailTemplateName) {
        const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
        setEmailTemplateName(nameWithoutExtension);
      }
      
      setSuccess(`File "${file.name}" loaded successfully (${(file.size / 1024).toFixed(2)} KB)`);
    } catch (err: any) {
      setError('Failed to read file. Please try again.');
    } finally {
      setLoading(false);
      if (templateFileInputRef.current) {
        templateFileInputRef.current.value = '';
      }
    }
  };

  const loadEmailTemplate = (template: MktEmail) => {
    setEmailTemplateName(template.mktemailname);
    setCampaignForm({
      ...campaignForm,
      emailsubject: template.mktemailname,
      emailtemplateid: template.mktemailid
    });
    setActiveTab('campaigns');
  };

  const handlePreviewTemplate = () => {
    if (!emailCode) {
      setError('No template code to preview');
      return;
    }

    if (emailCodeType === 'json') {
      setError('JSON preview not supported yet. Please use HTML templates for preview.');
      return;
    }

    setPreviewHtml(emailCode);
    setShowPreviewModal(true);
  };

  const toggleContactSelection = (pid: number) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(pid)) {
      newSelected.delete(pid);
    } else {
      newSelected.add(pid);
    }
    setSelectedContacts(newSelected);
  };

  const selectAllContacts = () => {
    const validContacts = contacts.filter(c => c.EMAIL && !c.EMAILOPTOUT && !c.CONVERTEDTOUSER);
    setSelectedContacts(new Set(validContacts.map(c => c.PID)));
  };

  const clearSelection = () => {
    setSelectedContacts(new Set());
  };

  const selectByRelation = (professionCode: string) => {
    const relationContacts = contacts.filter(c => c.EMAIL && !c.EMAILOPTOUT && !c.CONVERTEDTOUSER && getContactProfession(c, professions).code === professionCode);
    const newSelected = new Set(selectedContacts);
    relationContacts.forEach(c => newSelected.add(c.PID));
    setSelectedContacts(newSelected);
  };

  const selectByState = (state: string) => {
    const stateContacts = contacts.filter(c => c.EMAIL && !c.EMAILOPTOUT && !c.CONVERTEDTOUSER && c.STATE === state);
    const newSelected = new Set(selectedContacts);
    stateContacts.forEach(c => newSelected.add(c.PID));
    setSelectedContacts(newSelected);
  };

  // Filter and sort contacts for recipient selection
  const getFilteredAndSortedContacts = () => {
    let filtered = contacts.filter(c => c.EMAIL && !c.EMAILOPTOUT && !c.CONVERTEDTOUSER);

    // Apply search filter
    if (recipientFilters.search) {
      const searchLower = recipientFilters.search.toLowerCase();
      filtered = filtered.filter(c => 
        (c.FIRST?.toLowerCase().includes(searchLower)) ||
        (c.LAST?.toLowerCase().includes(searchLower)) ||
        (c.EMAIL?.toLowerCase().includes(searchLower)) ||
        (c.COMPANY?.toLowerCase().includes(searchLower))
      );
    }

    // Apply state filter
    if (recipientFilters.state) {
      filtered = filtered.filter(c => c.STATE === recipientFilters.state);
    }

    // Apply profession filter (dropdown value is the canonical professionID, or
    // legacy display text for the handful of contacts not yet backfilled)
    if (recipientFilters.relation) {
      filtered = filtered.filter(c => getContactProfession(c, professions).code === recipientFilters.relation);
    }

    // Apply list filter — union across selected lists (a contact in ANY chosen
    // list matches), so checking multiple lists sends to their combination.
    if (recipientFilters.lists.length > 0) {
      filtered = filtered.filter(c => (c.LISTS || []).some(l => recipientFilters.lists.includes(l)));
    }

    // Sort contacts
    filtered.sort((a, b) => {
      let compareA: string = '';
      let compareB: string = '';

      switch (recipientSortBy) {
        case 'name':
          compareA = `${a.FIRST} ${a.LAST}`.toLowerCase();
          compareB = `${b.FIRST} ${b.LAST}`.toLowerCase();
          break;
        case 'email':
          compareA = (a.EMAIL || '').toLowerCase();
          compareB = (b.EMAIL || '').toLowerCase();
          break;
        case 'state':
          compareA = (a.STATE || '').toLowerCase();
          compareB = (b.STATE || '').toLowerCase();
          break;
        case 'relation':
          compareA = getContactProfession(a, professions).code.toLowerCase();
          compareB = getContactProfession(b, professions).code.toLowerCase();
          break;
      }

      if (recipientSortOrder === 'asc') {
        return compareA.localeCompare(compareB);
      } else {
        return compareB.localeCompare(compareA);
      }
    });

    return filtered;
  };

  // Group contacts by relation
  const getContactsByRelation = () => {
    const filtered = getFilteredAndSortedContacts();
    const grouped = new Map<string, typeof filtered>();

    filtered.forEach(contact => {
      const relation = getContactProfession(contact, professions).code;
      if (!grouped.has(relation)) {
        grouped.set(relation, []);
      }
      grouped.get(relation)!.push(contact);
    });

    // Sort groups by relation code
    return Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h1 className="mb-0">Marketing Email Admin</h1>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowTestRecipientsPanel(p => !p)}
        >
          {showTestRecipientsPanel ? 'Hide' : 'Test Recipients'} ({testRecipients.length})
        </button>
      </div>

      {showTestRecipientsPanel && (
        <div className="card mb-3 border-warning">
          <div className="card-body py-3">
            <h6 className="mb-2 fw-semibold">Test Email Recipients</h6>
            <p className="text-muted small mb-2">
              These addresses receive emails when you click "Send Test" in the preview modal.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {testRecipients.map(email => (
                <span key={email} className="badge bg-secondary d-flex align-items-center gap-1" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  {email}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => removeTestRecipient(email)}
                    aria-label="Remove"
                  />
                </span>
              ))}
              {testRecipients.length === 0 && <span className="text-muted small">No recipients configured</span>}
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
              <input
                type="email"
                className="form-control"
                placeholder="Add email address"
                value={newTestRecipientInput}
                onChange={e => setNewTestRecipientInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTestRecipient(); } }}
              />
              <button className="btn btn-outline-primary" type="button" onClick={addTestRecipient}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible">
          {error}
          <button className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible">
          {success}
          <button className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {showProgress && (
        <div className="card mb-3">
          <div className="card-body">
            <h5>Send Sending Campaign...</h5>
            <div className="progress">
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated" 
                style={{ width: `${sendProgress}%` }}
              >
                {sendProgress}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'campaigns' ? 'active' : ''}`}
            onClick={() => setActiveTab('campaigns')}
          >
             Campaigns ({campaigns.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
             Contacts ({contacts.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => setActiveTab('emails')}
          >
             Templates ({mktEmails.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'lists' ? 'active' : ''}`}
            onClick={() => setActiveTab('lists')}
          >
             Lists ({lists.length})
          </button>
        </li>
      </ul>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3> Campaigns</h3>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setShowCampaignForm(true);
                setEditingCampaign(null);
                setCampaignForm({ 
                  campaignname: '', 
                  campaignnote: '', 
                  emailsubject: '', 
                  emailbody: '', 
                  emailtemplateid: undefined,
                  campaignsentto: [] 
                });
                setSelectedContacts(new Set());
              }}
            >
              New Campaign
            </button>
          </div>

          {showCampaignForm && (
            <div className="card mb-4">
              <div className="card-body">
                <h5>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h5>
                <form onSubmit={handleCreateCampaign}>
                  <div className="mb-3">
                    <label className="form-label">Campaign Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={campaignForm.campaignname}
                      onChange={(e) => setCampaignForm({ ...campaignForm, campaignname: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email Template (optional)</label>
                    <select
                      className="form-select"
                      value={campaignForm.emailtemplateid || ''}
                      onChange={(e) => {
                        const templateId = e.target.value ? parseInt(e.target.value) : undefined;
                        const template = mktEmails.find(t => t.mktemailid === templateId);
                        
                        if (template && template.html) {
                          // Use template as-is without adding header
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailtemplateid: templateId,
                            emailsubject: template?.mktemailname || campaignForm.emailsubject,
                            emailbody: template.html
                          });
                        } else {
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailtemplateid: templateId,
                            emailsubject: template?.mktemailname || campaignForm.emailsubject,
                            emailbody: campaignForm.emailbody
                          });
                        }
                      }}
                    >
                      <option value="">None - Write custom email</option>
                      {mktEmails.map(template => (
                        <option key={template._id} value={template.mktemailid}>
                          {template.mktemailname}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Template will automatically include "Hello {'{FIRST}'} {'{LAST}'}," header
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email Subject *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={campaignForm.emailsubject}
                      onChange={(e) => setCampaignForm({ ...campaignForm, emailsubject: e.target.value })}
                      required
                      placeholder="Enter email subject line"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email Body (HTML) *</label>
                    <textarea
                      id="emailBodyTextarea"
                      className="form-control font-monospace"
                      rows={10}
                      value={campaignForm.emailbody}
                      onChange={(e) => setCampaignForm({ ...campaignForm, emailbody: e.target.value })}
                      required
                      placeholder="<h1>Hello {FIRST} {LAST}!</h1><p>Your content here...</p>"
                    />
                    <small className="text-muted d-block mb-2">
                      Click to insert placeholder:
                    </small>
                    <div className="btn-group btn-group-sm mb-2" role="group">
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const textarea = document.getElementById('emailBodyTextarea') as HTMLTextAreaElement;
                          const cursorPos = textarea.selectionStart;
                          const textBefore = campaignForm.emailbody.substring(0, cursorPos);
                          const textAfter = campaignForm.emailbody.substring(cursorPos);
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailbody: textBefore + '{FIRST}' + textAfter 
                          });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + 7, cursorPos + 7);
                          }, 0);
                        }}
                      >
                        {'{FIRST}'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const textarea = document.getElementById('emailBodyTextarea') as HTMLTextAreaElement;
                          const cursorPos = textarea.selectionStart;
                          const textBefore = campaignForm.emailbody.substring(0, cursorPos);
                          const textAfter = campaignForm.emailbody.substring(cursorPos);
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailbody: textBefore + '{LAST}' + textAfter 
                          });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + 6, cursorPos + 6);
                          }, 0);
                        }}
                      >
                        {'{LAST}'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const textarea = document.getElementById('emailBodyTextarea') as HTMLTextAreaElement;
                          const cursorPos = textarea.selectionStart;
                          const textBefore = campaignForm.emailbody.substring(0, cursorPos);
                          const textAfter = campaignForm.emailbody.substring(cursorPos);
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailbody: textBefore + '{EMAIL}' + textAfter 
                          });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + 7, cursorPos + 7);
                          }, 0);
                        }}
                      >
                        {'{EMAIL}'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const textarea = document.getElementById('emailBodyTextarea') as HTMLTextAreaElement;
                          const cursorPos = textarea.selectionStart;
                          const textBefore = campaignForm.emailbody.substring(0, cursorPos);
                          const textAfter = campaignForm.emailbody.substring(cursorPos);
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailbody: textBefore + '{COMPANY}' + textAfter 
                          });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + 9, cursorPos + 9);
                          }, 0);
                        }}
                      >
                        {'{COMPANY}'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const textarea = document.getElementById('emailBodyTextarea') as HTMLTextAreaElement;
                          const cursorPos = textarea.selectionStart;
                          const textBefore = campaignForm.emailbody.substring(0, cursorPos);
                          const textAfter = campaignForm.emailbody.substring(cursorPos);
                          setCampaignForm({ 
                            ...campaignForm, 
                            emailbody: textBefore + '{TITLE}' + textAfter 
                          });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + 7, cursorPos + 7);
                          }, 0);
                        }}
                      >
                        {'{TITLE}'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Recipients * ({selectedContacts.size} selected)</label>
                    
                    {/* Search and Filters */}
                    <div className="row g-2 mb-3">
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="🔍 Search names, emails, companies..."
                          value={recipientFilters.search}
                          onChange={(e) => setRecipientFilters({ ...recipientFilters, search: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <select
                          className="form-select form-select-sm"
                          value={recipientFilters.state}
                          onChange={(e) => setRecipientFilters({ ...recipientFilters, state: e.target.value })}
                        >
                          <option value="">All States</option>
                          {uniqueStates.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select
                          className="form-select form-select-sm"
                          value={recipientFilters.relation}
                          onChange={(e) => setRecipientFilters({ ...recipientFilters, relation: e.target.value })}
                        >
                          <option value="">All Relations</option>
                          {uniqueRelations.map(rel => (
                            <option key={rel.code} value={rel.code}>
                              {rel.code} - {rel.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* List filter — check multiple lists to send to their combination */}
                    {lists.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex flex-wrap align-items-center gap-3 p-2 border rounded" style={{ background: '#f9fafb' }}>
                          <span className="small fw-semibold text-muted">Lists:</span>
                          {lists.map(list => (
                            <div key={list.listID} className="form-check form-check-inline m-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`recipient-list-${list.listID}`}
                                checked={recipientFilters.lists.includes(list.listID)}
                                onChange={(e) => {
                                  const nextLists = e.target.checked
                                    ? [...recipientFilters.lists, list.listID]
                                    : recipientFilters.lists.filter(id => id !== list.listID);
                                  setRecipientFilters({ ...recipientFilters, lists: nextLists });
                                }}
                              />
                              <label className="form-check-label small" htmlFor={`recipient-list-${list.listID}`}>
                                {list.name} ({list.contactCount ?? 0})
                              </label>
                            </div>
                          ))}
                          {recipientFilters.lists.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary py-0"
                              onClick={() => {
                                const filteredPids = getFilteredAndSortedContacts().map(c => c.PID);
                                const newSelected = new Set(selectedContacts);
                                filteredPids.forEach(pid => newSelected.add(pid));
                                setSelectedContacts(newSelected);
                              }}
                            >
                              Select All in Checked Lists
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sort and View Options */}
                    <div className="row g-2 mb-2">
                      <div className="col-md-4">
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">Sort by</span>
                          <select
                            className="form-select form-select-sm"
                            value={recipientSortBy}
                            onChange={(e) => setRecipientSortBy(e.target.value as any)}
                          >
                            <option value="relation">Relation</option>
                            <option value="name">Name</option>
                            <option value="email">Email</option>
                            <option value="state">State</option>
                          </select>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => setRecipientSortOrder(recipientSortOrder === 'asc' ? 'desc' : 'asc')}
                          >
                            {recipientSortOrder === 'asc' ? '↑' : '↓'}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="btn-group btn-group-sm w-100" role="group">
                          <button
                            type="button"
                            className={`btn ${recipientViewMode === 'flat' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setRecipientViewMode('flat')}
                          >
                            Flat List
                          </button>
                          <button
                            type="button"
                            className={`btn ${recipientViewMode === 'grouped' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setRecipientViewMode('grouped')}
                          >
                            Grouped
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="btn-group btn-group-sm w-100">
                          <button type="button" className="btn btn-outline-primary" onClick={selectAllContacts}>
                            Select All ({contacts.filter(c => c.EMAIL && !c.EMAILOPTOUT && !c.CONVERTEDTOUSER).length})
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={clearSelection}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Contact List */}
                    <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {recipientViewMode === 'grouped' ? (
                        // Grouped View by Relation
                        getContactsByRelation().map(([relation, groupContacts]) => {
                          const allSelected = groupContacts.every(c => selectedContacts.has(c.PID));
                          const someSelected = groupContacts.some(c => selectedContacts.has(c.PID)) && !allSelected;
                          
                          return (
                            <div key={relation} className="mb-3">
                              <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                                <strong className="me-2">
                                  <span className="badge bg-primary">{relation}</span> {groupContacts[0] ? getContactProfession(groupContacts[0], professions).name : relation}
                                </strong>
                                <span className="text-muted small me-auto">({groupContacts.length} contacts)</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => selectByRelation(relation)}
                                >
                                  Select All {relation}
                                </button>
                              </div>
                              {groupContacts.map(contact => (
                                <div key={contact._id} className="form-check ms-3 mb-1">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedContacts.has(contact.PID)}
                                    onChange={() => toggleContactSelection(contact.PID)}
                                  />
                                  <label className="form-check-label small">
                                    {contact.FIRST} {contact.LAST} ({contact.EMAIL})
                                    {contact.STATE && <span className="text-muted"> - {contact.STATE}</span>}
                                    {contact.COMPANY && <span className="text-muted"> - {contact.COMPANY}</span>}
                                  </label>
                                </div>
                              ))}
                            </div>
                          );
                        })
                      ) : (
                        // Flat List View
                        getFilteredAndSortedContacts().map(contact => (
                          <div key={contact._id} className="form-check mb-1">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedContacts.has(contact.PID)}
                              onChange={() => toggleContactSelection(contact.PID)}
                            />
                            <label className="form-check-label small">
                              <span className="badge bg-primary me-1">{getContactProfession(contact, professions).code}</span>
                              {contact.FIRST} {contact.LAST} ({contact.EMAIL})
                              {contact.STATE && <span className="text-muted"> - {contact.STATE}</span>}
                              {contact.COMPANY && <span className="text-muted"> - {contact.COMPANY}</span>}
                            </label>
                          </div>
                        ))
                      )}
                      {getFilteredAndSortedContacts().length === 0 && (
                        <p className="text-muted mb-0">No contacts match the current filters</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={campaignForm.campaignnote}
                      onChange={(e) => setCampaignForm({ ...campaignForm, campaignnote: e.target.value })}
                      placeholder="Internal notes about this campaign"
                    />
                  </div>

                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowCampaignForm(false);
                        setEditingCampaign(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Recipients</th>
                  <th>Unsubs</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Scheduled/Sent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No campaigns yet. Click "New Campaign" to create one!
                    </td>
                  </tr>
                ) : (
                  campaigns.map(campaign => (
                    <tr key={campaign._id}>
                      <td><strong>{campaign.campaignname}</strong></td>
                      <td>{campaign.emailsubject}</td>
                      <td>{campaign.campaignsentto?.length || 0}</td>
                      <td>
                        {campaign.status === 'sent' ? (
                          unsubCounts[campaign._id] !== undefined ? (
                            unsubCounts[campaign._id] > 0 ? (
                              <button
                                className="btn btn-sm btn-link p-0 text-danger fw-semibold"
                                style={{ textDecoration: 'none' }}
                                onClick={() => openUnsubModal(campaign)}
                                title="View unsubscribers from this campaign"
                              >
                                {unsubCounts[campaign._id]}
                              </button>
                            ) : (
                              <span className="text-muted small">0</span>
                            )
                          ) : (
                            <span className="text-muted small">…</span>
                          )
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${
                          campaign.status === 'sent' ? 'success' :
                          campaign.status === 'scheduled' ? 'info' : 
                          campaign.status === 'cancelled' ? 'danger' : 'secondary'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td>{new Date(campaign.campaigninput).toLocaleDateString()}</td>
                      <td>
                        {campaign.status === 'scheduled' && campaign.scheduledDate && (
                          <span className="text-info">
                            <i className="bi bi-clock"></i> {new Date(campaign.scheduledDate).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                          </span>
                        )}
                        {campaign.status === 'sent' && campaign.campaignsent && (
                          <span className="text-success">
                            <i className="bi bi-check-circle"></i> {new Date(campaign.campaignsent).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                          </span>
                        )}
                        {(campaign.status === 'draft' || campaign.status === 'cancelled') && (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          {/* Always show Edit button */}
                          <button 
                            className="btn btn-outline-primary"
                            onClick={() => handleEditCampaign(campaign)}
                            title="Edit campaign"
                          >
                            Edit
                          </button>
                          
                          {/* Always show Duplicate button */}
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={() => handleDuplicateCampaign(campaign)}
                            title="Create a copy"
                          >
                            Duplicate
                          </button>
                          
                          {/* Always show Preview/View Details */}
                          <button 
                            className="btn btn-outline-info"
                            onClick={() => openPreviewModal(campaign)}
                            title={campaign.status === 'sent' ? 'View sent content' : 'Preview email'}
                          >
                            {campaign.status === 'sent' || campaign.status === 'scheduled' ? 'View' : 'Preview'}
                          </button>

                          {/* Results — sent/failed breakdown, only meaningful once a send was attempted */}
                          {campaign.status === 'sent' && (
                            <button
                              className="btn btn-outline-dark"
                              onClick={() => openResultsModal(campaign)}
                              title="View send results (sent/failed breakdown)"
                            >
                              Results
                            </button>
                          )}

                          {/* Show Send/Test only for draft or scheduled */}
                          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                            <>
                              <button 
                                className="btn btn-outline-success"
                                onClick={() => openTestEmailModal(campaign._id)}
                                title="Send test email"
                              >
                                Test
                              </button>
                              <button 
                                className="btn btn-outline-warning"
                                onClick={() => openScheduleModal(campaign._id)}
                                title="Schedule or send now"
                              >
                                {campaign.status === 'scheduled' ? 'Reschedule' : 'Send'}
                              </button>
                              {campaign.status === 'scheduled' && (
                                <button 
                                  className="btn btn-outline-secondary"
                                  onClick={() => handleCancelSchedule(campaign._id)}
                                  title="Cancel scheduled send"
                                >
                                  Cancel Send
                                </button>
                              )}
                            </>
                          )}
                          
                          {/* Always show Delete */}
                          <button 
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteCampaign(campaign._id)}
                            title="Delete campaign"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Contacts</h3>
            <div>
              <button 
                className="btn btn-secondary me-2"
                onClick={() => setShowCSVImportModal(true)}
              >
                Import CSV
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowContactForm(true);
                  setEditingContact(null);
                  setContactForm({
                    FIRST: '',
                    LAST: '',
                    EMAIL: '',
                    COMPANY: '',
                    TITLE: '',
                    STATE: '',
                    PROFESSIONID: '',
                    NOTE: '',
                    EMAILOPTOUT: false
                  });
                }}
              >
                New Contact
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCSVImport}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="🔍 Search name/email/company"
                    value={contactFilters.search}
                    onChange={async (e) => {
                      const newFilters = { ...contactFilters, search: e.target.value };
                      setContactFilters(newFilters);
                      try {
                        const data = await MarketingAPI.getAllContacts(newFilters);
                        setContacts(data || []);
                      } catch (err: any) {
                        console.error('Error loading contacts:', err);
                        setContacts([]);
                      }
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={contactFilters.state}
                    onChange={async (e) => {
                      const newFilters = { ...contactFilters, state: e.target.value };
                      setContactFilters(newFilters);
                      try {
                        const data = await MarketingAPI.getAllContacts(newFilters);
                        setContacts(data || []);
                      } catch (err: any) {
                        console.error('Error loading contacts:', err);
                        setContacts([]);
                      }
                    }}
                  >
                    <option value="">All States</option>
                    {uniqueStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={contactFilters.relation}
                    onChange={async (e) => {
                      const newFilters = { ...contactFilters, relation: e.target.value };
                      setContactFilters(newFilters);
                      try {
                        const data = await MarketingAPI.getAllContacts(newFilters);
                        setContacts(data || []);
                      } catch (err: any) {
                        console.error('Error loading contacts:', err);
                        setContacts([]);
                      }
                    }}
                  >
                    <option value="">All Relations</option>
                    {uniqueRelations.map(rel => (
                      <option key={rel.code} value={rel.code}>
                        {rel.code} - {rel.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={contactFilters.lists[0] ?? ''}
                    onChange={async (e) => {
                      const newFilters = { ...contactFilters, lists: e.target.value ? [parseInt(e.target.value, 10)] : [] };
                      setContactFilters(newFilters);
                      try {
                        const data = await MarketingAPI.getAllContacts(newFilters);
                        setContacts(data || []);
                      } catch (err: any) {
                        console.error('Error loading contacts:', err);
                        setContacts([]);
                      }
                    }}
                  >
                    <option value="">All Lists</option>
                    {lists.map(list => (
                      <option key={list.listID} value={list.listID}>{list.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={contactFilters.emailOptout}
                      onChange={async (e) => {
                        const newFilters = { ...contactFilters, emailOptout: e.target.checked };
                        setContactFilters(newFilters);
                        try {
                          const data = await MarketingAPI.getAllContacts(newFilters);
                          setContacts(data || []);
                        } catch (err: any) {
                          console.error('Error loading contacts:', err);
                          setContacts([]);
                        }
                      }}
                    />
                    <label className="form-check-label">Show Unsubscribed Only</label>
                  </div>
                </div>
                <div className="col-md-2">
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={() => {
                      setContactFilters({ search: '', state: '', relation: '', emailOptout: false, lists: [] });
                      fetchContacts();
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {showContactForm && (
            <div className="card mb-4">
              <div className="card-body">
                <h5>{editingContact ? 'Edit Edit Contact' : 'Add New Contact'}</h5>
                <form onSubmit={handleContactFormSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contactForm.FIRST}
                        onChange={(e) => setContactForm({ ...contactForm, FIRST: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contactForm.LAST}
                        onChange={(e) => setContactForm({ ...contactForm, LAST: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={contactForm.EMAIL}
                        onChange={(e) => setContactForm({ ...contactForm, EMAIL: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contactForm.COMPANY}
                        onChange={(e) => setContactForm({ ...contactForm, COMPANY: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contactForm.TITLE}
                        onChange={(e) => setContactForm({ ...contactForm, TITLE: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contactForm.STATE}
                        onChange={(e) => setContactForm({ ...contactForm, STATE: e.target.value })}
                        maxLength={2}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Profession</label>
                      <select
                        className="form-select"
                        value={contactForm.PROFESSIONID}
                        onChange={(e) => setContactForm({ ...contactForm, PROFESSIONID: e.target.value })}
                      >
                        <option value="">— Select —</option>
                        {professions.map(prof => (
                          <option key={prof.professionID} value={prof.professionID}>
                            {prof.name} ({prof.professionID})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Memo</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Internal note about this contact"
                        value={contactForm.NOTE}
                        onChange={(e) => setContactForm({ ...contactForm, NOTE: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={contactForm.EMAILOPTOUT}
                          onChange={(e) => setContactForm({ ...contactForm, EMAILOPTOUT: e.target.checked })}
                        />
                        <label className="form-check-label">Email Opt-out (Unsubscribed)</label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : editingContact ? 'Update' : 'Add Contact'}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowContactForm(false);
                        setEditingContact(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk action bar */}
          {checkedIds.size > 0 && (
            <div className="d-flex align-items-center gap-3 px-3 py-2 mb-2 rounded"
              style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                {checkedIds.size} contact{checkedIds.size !== 1 ? 's' : ''} selected
              </span>
              <div className="input-group input-group-sm" style={{ width: 'auto' }}>
                <select
                  className="form-select form-select-sm"
                  value={addToListTarget}
                  onChange={(e) => setAddToListTarget(e.target.value)}
                >
                  <option value="">Add to list…</option>
                  {lists.map(list => (
                    <option key={list.listID} value={list._id}>{list.name}</option>
                  ))}
                </select>
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={!addToListTarget}
                  onClick={handleAddSelectedToList}
                >
                  Add
                </button>
              </div>
              <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
                Delete Selected
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setCheckedIds(new Set())}>
                Clear
              </button>
            </div>
          )}

          <div className="d-flex align-items-center mb-2 text-muted small">
            Showing <strong className="mx-1">{contacts.length.toLocaleString()}</strong> contact{contacts.length !== 1 ? 's' : ''}
          </div>

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={contacts.length > 0 && checkedIds.size === contacts.length}
                      ref={el => { if (el) el.indeterminate = checkedIds.size > 0 && checkedIds.size < contacts.length; }}
                      onChange={toggleCheckAll}
                      title="Select all"
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Title</th>
                  <th>State</th>
                  <th>Profession</th>
                  <th>Memo</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      No contacts found. Add contacts manually or import from CSV.
                    </td>
                  </tr>
                ) : (
                  contacts.map(contact => (
                    <tr key={contact._id} style={{ background: checkedIds.has(contact._id) ? '#f0f9ff' : undefined }}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checkedIds.has(contact._id)}
                          onChange={() => toggleChecked(contact._id)}
                        />
                      </td>
                      <td>{contact.FIRST} {contact.LAST}</td>
                      <td>{contact.EMAIL}</td>
                      <td>{contact.COMPANY}</td>
                      <td>{contact.TITLE}</td>
                      <td>{contact.STATE}</td>
                      <td>
                        <span className="badge bg-primary" title={getContactProfession(contact, professions).name}>
                          {getContactProfession(contact, professions).code}
                        </span>
                      </td>
                      <td className="small text-muted" style={{ maxWidth: 160 }} title={contact.NOTE}>
                        {contact.NOTE ? (contact.NOTE.length > 40 ? contact.NOTE.slice(0, 40) + '…' : contact.NOTE) : '—'}
                      </td>
                      <td>
                        {contact.CONVERTEDTOUSER ? (
                          <span className="badge bg-info text-dark" title="Registered a CEC Track account — no longer a marketing prospect">Converted</span>
                        ) : contact.EMAILOPTOUT ? (
                          <span className="badge bg-danger">Unsubscribed</span>
                        ) : (
                          <span className="badge bg-success">Subscribed</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-info"
                            onClick={() => openEmailHistoryModal(contact)}
                            title="View Email History"
                          >
                            📧 History
                          </button>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEditContact(contact)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteContact(contact._id)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Templates Tab */}
      {activeTab === 'emails' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Email Templates</h3>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success"
                onClick={() => { setShowAIModal(true); setAiStep('form'); setAiResult(null); loadTopLogo(); }}
              >
                ✨ AI Generate
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (showEmailForm && !editingTemplate) {
                    setShowEmailForm(false);
                  } else {
                    setShowEmailForm(true);
                    setEditingTemplate(null);
                    setEmailTemplateName('');
                    setEmailCode('');
                    setEmailCodeType('html');
                  }
                }}
              >
                {showEmailForm && !editingTemplate ? 'Cancel' : 'New Template'}
              </button>
            </div>
          </div>

          {/* ── AI Generate Modal ── */}
          {showAIModal && (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}>
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">

                  {/* Header */}
                  <div className="modal-header" style={{ background: 'linear-gradient(135deg,#0F1F3D,#1a3560)', color: '#fff' }}>
                    <h5 className="modal-title">✨ Generate Email with AI</h5>
                    <button type="button" className="btn-close btn-close-white"
                      onClick={() => { setShowAIModal(false); setAiStep('form'); setAiResult(null); clearHeroImage(); }} />
                  </div>

                  {/* ── FORM step ── */}
                  {aiStep === 'form' && (
                    <div className="modal-body">
                      <p className="text-muted small mb-4">
                        GPT-4 writes the full branded email HTML. Optionally generates a hero image via gpt-image-1 (~$0.04 extra).
                      </p>

                      {/* Topic */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Topic / Subject <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder='e.g. "Welcome new architect members" or "Year-end CE deadline reminder for CPAs"'
                          value={aiForm.topic}
                          onChange={e => setAiForm(f => ({ ...f, topic: e.target.value }))}
                          autoFocus
                        />
                        <div className="form-text">Be specific — the more detail, the better the email.</div>
                      </div>

                      <div className="row g-3 mb-3">
                        {/* Purpose */}
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Email Purpose</label>
                          <select className="form-select" value={aiForm.purpose}
                            onChange={e => setAiForm(f => ({ ...f, purpose: e.target.value as AIEmailRequest['purpose'] }))}>
                            <option value="newsletter">📰 Monthly Newsletter</option>
                            <option value="welcome">👋 Welcome / Onboarding</option>
                            <option value="reminder">⏰ CE Deadline Reminder</option>
                            <option value="promotional">🎁 Promotional Offer</option>
                            <option value="announcement">📣 Feature Announcement</option>
                            <option value="reengagement">🔄 Re-engagement</option>
                            <option value="campaign">🎯 Targeted Campaign</option>
                          </select>
                        </div>

                        {/* Audience */}
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Target Audience</label>
                          <select className="form-select" value={aiForm.targetAudience}
                            onChange={e => setAiForm(f => ({ ...f, targetAudience: e.target.value }))}>
                            <option value="all">👥 All Licensed Professionals</option>
                            <option value="general">🌐 General / Prospects</option>
                            <option value="ARCH">🏛️ Architects</option>
                            <option value="PE">⚙️ Professional Engineers</option>
                            <option value="CPA">📊 Certified Public Accountants</option>
                            <option value="LAW">⚖️ Attorneys</option>
                            <option value="RN">🏥 Nurses / Healthcare</option>
                            <option value="LA">🌿 Landscape Architects</option>
                            <option value="LMHC">🧠 Mental Health Counselors</option>
                            <option value="PHARM">💊 Pharmacists</option>
                            <option value="DDS">🦷 Dentists</option>
                          </select>
                        </div>

                        {/* Tone */}
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Tone</label>
                          <select className="form-select" value={aiForm.tone}
                            onChange={e => setAiForm(f => ({ ...f, tone: e.target.value as AIEmailRequest['tone'] }))}>
                            <option value="professional">🎩 Professional</option>
                            <option value="friendly">😊 Friendly & Warm</option>
                            <option value="urgent">🔔 Urgent / Reminder</option>
                          </select>
                        </div>
                      </div>

                      {/* ── Persistent Top Logo ── */}
                      <div className="card mb-3" style={{ border: '1px solid #d1d5db', background: '#fafafa' }}>
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Top Logo</span>
                              <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>Persistent · appears above every generated email</span>
                            </div>
                            {topLogoLoading && <span className="spinner-border spinner-border-sm text-secondary" />}
                          </div>

                          {topLogoUrl ? (
                            <div>
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <img
                                  src={topLogoUrl}
                                  alt="Top logo"
                                  style={{ height: 52, maxWidth: 200, objectFit: 'contain', borderRadius: 4, border: '1px solid #e5e7eb', background: '#fff', padding: 2 }}
                                />
                                <div className="flex-grow-1">
                                  <div className="text-success small fw-semibold">✓ Active — will appear at top of all generated emails</div>
                                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>Stored in S3 · persists across sessions</div>
                                </div>
                                <div className="d-flex gap-2">
                                  <input ref={topLogoFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleTopLogoFileSelect} />
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => topLogoFileInputRef.current?.click()} disabled={topLogoUploading}>
                                    {topLogoUploading ? '⏳' : '↑ Change'}
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleDeleteTopLogo}>
                                    Remove
                                  </button>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>Links to:</span>
                                <input
                                  type="url"
                                  className="form-control form-control-sm"
                                  value={topLogoLink}
                                  onChange={e => setTopLogoLink(e.target.value)}
                                  placeholder="https://cectrack.com"
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <div style={{ width: 80, height: 40, background: '#e5e7eb', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No logo</span>
                                </div>
                                <div className="flex-grow-1 text-muted small">
                                  No top logo set. Upload a banner image (e.g. branded letterhead or masthead) that will always appear at the top of generated emails.
                                </div>
                                <div>
                                  <input ref={topLogoFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleTopLogoFileSelect} />
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => topLogoFileInputRef.current?.click()} disabled={topLogoUploading}>
                                    {topLogoUploading ? '⏳ Uploading…' : '↑ Upload Top Logo'}
                                  </button>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>Links to:</span>
                                <input
                                  type="url"
                                  className="form-control form-control-sm"
                                  value={topLogoLink}
                                  onChange={e => setTopLogoLink(e.target.value)}
                                  placeholder="https://cectrack.com"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── Color Scheme ── */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Color Scheme</label>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            { value: 'navy-teal',          header: '#0F1F3D', accent: '#14b8a6', label: 'Navy & Teal' },
                            { value: 'navy-amber',         header: '#0F1F3D', accent: '#f59e0b', label: 'Navy & Amber' },
                            { value: 'forest-gold',        header: '#1a3a2a', accent: '#c9a227', label: 'Forest & Gold' },
                            { value: 'slate-sky',          header: '#1e293b', accent: '#0ea5e9', label: 'Slate & Sky' },
                            { value: 'burgundy-champagne', header: '#4a1020', accent: '#e8c87d', label: 'Burgundy & Champagne' },
                          ].map(cs => (
                            <button
                              key={cs.value}
                              type="button"
                              onClick={() => setAiForm(f => ({ ...f, colorScheme: cs.value as AIEmailRequest['colorScheme'] }))}
                              style={{
                                border: aiForm.colorScheme === cs.value ? '2px solid #374151' : '2px solid #e5e7eb',
                                borderRadius: 8,
                                padding: '6px 10px',
                                background: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: aiForm.colorScheme === cs.value ? '0 0 0 2px rgba(55,65,81,0.15)' : 'none',
                                transition: 'border-color 0.15s',
                              }}
                              title={cs.label}
                            >
                              <span style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', width: 32, height: 20, flexShrink: 0 }}>
                                <span style={{ background: cs.header, flex: 1, display: 'block' }} />
                                <span style={{ background: cs.accent, flex: 1, display: 'block' }} />
                              </span>
                              <span style={{ fontSize: '0.78rem', color: '#374151', whiteSpace: 'nowrap' }}>{cs.label}</span>
                              {aiForm.colorScheme === cs.value && <span style={{ color: '#374151', fontSize: '0.75rem' }}>✓</span>}
                            </button>
                          ))}
                        </div>
                        <div className="form-text mt-1">Colors are applied to the header, accents, CTA button, and footer using color theory pairings.</div>
                      </div>

                      {/* Image options */}
                      <div className="card mb-3" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            <div className="form-check form-switch mb-0">
                              <input className="form-check-input" type="checkbox" role="switch"
                                id="includeImageSwitch"
                                checked={aiForm.includeImage}
                                onChange={e => setAiForm(f => ({ ...f, includeImage: e.target.checked }))} />
                              <label className="form-check-label fw-semibold" htmlFor="includeImageSwitch">
                                Include AI hero image
                              </label>
                            </div>
                            <span className="badge bg-warning text-dark">+~$0.04</span>
                          </div>
                          {aiForm.includeImage && (
                            <div className="mt-2">
                              {/* AI generation options — hidden when file is uploaded */}
                              {!heroImageFile && (
                                <div className="row g-2 mb-3">
                                  <div className="col-md-5">
                                    <label className="form-label small text-muted mb-1">Image Style</label>
                                    <select className="form-select form-select-sm" value={aiForm.imageStyle}
                                      onChange={e => setAiForm(f => ({ ...f, imageStyle: e.target.value as AIEmailRequest['imageStyle'] }))}>
                                      <option value="auto">🎲 Auto (random)</option>
                                      <option value="photorealistic">📷 Photorealistic lifestyle</option>
                                      <option value="infographic">📊 Data infographic</option>
                                      <option value="simple">✏️ Bold minimal graphic</option>
                                    </select>
                                  </div>
                                  <div className="col-md-7 d-flex align-items-end">
                                    <p className="text-muted small mb-0">
                                      Image is generated after content and placed as a hero banner.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Divider */}
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <hr className="flex-grow-1 my-0" />
                                <span className="text-muted small">{heroImageFile ? '✓ Using uploaded image' : 'or upload your own'}</span>
                                <hr className="flex-grow-1 my-0" />
                              </div>

                              {/* File upload area */}
                              {!heroImageFile ? (
                                <div>
                                  <input
                                    ref={heroFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleHeroFileSelect}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => heroFileInputRef.current?.click()}
                                  >
                                    📁 Choose Image File
                                  </button>
                                  <span className="text-muted small ms-2">PNG, JPG, WebP — max 8 MB. Overrides AI generation.</span>
                                </div>
                              ) : (
                                <div>
                                  <div className="d-flex align-items-center gap-3 p-2 rounded mb-2" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                                    {heroImagePreview && (
                                      <img src={heroImagePreview} alt="Hero preview" style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 4, border: '1px solid #ccc' }} />
                                    )}
                                    <div className="flex-grow-1">
                                      <div className="fw-semibold small">{heroImageFile.name}</div>
                                      <div className="text-muted small">
                                        {heroImageUploading ? '⏳ Uploading to S3…' : heroImageUrl ? '✓ Uploaded — ready to use' : ''}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={clearHeroImage}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>Links to:</span>
                                    <input
                                      type="url"
                                      className="form-control form-control-sm"
                                      value={heroImageLink}
                                      onChange={e => setHeroImageLink(e.target.value)}
                                      placeholder="https://cectrack.com"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Dashboard cards preview option */}
                      <div className="card mb-3" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="card-body py-3">
                          <div className="form-check form-switch mb-0">
                            <input className="form-check-input" type="checkbox" role="switch"
                              id="includeSampleCardsSwitch"
                              checked={aiForm.includeSampleCards || false}
                              onChange={e => setAiForm(f => ({ ...f, includeSampleCards: e.target.checked }))} />
                            <label className="form-check-label fw-semibold" htmlFor="includeSampleCardsSwitch">
                              Include sample dashboard preview
                            </label>
                          </div>
                          <p className="text-muted small mb-0 mt-1">Embeds 3 sample compliance cycle cards showcasing the app's main dashboard feature.</p>
                        </div>
                      </div>

                      {/* Additional context */}
                      <div className="mb-0">
                        <label className="form-label fw-semibold">Additional Context <span className="text-muted fw-normal">(optional)</span></label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder='e.g. "Mention the new CE Audit feature. Include a link to the pricing page. Keep it under 200 words."'
                          value={aiForm.additionalContext}
                          onChange={e => setAiForm(f => ({ ...f, additionalContext: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  {/* ── LOADING step ── */}
                  {aiStep === 'loading' && (
                    <div className="modal-body text-center py-5">
                      <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
                      <p className="fw-semibold fs-5 mb-1">Generating your email…</p>
                      <p className="text-muted">{aiLoadingMsg}</p>
                      <p className="text-muted small mt-3">GPT-4 is writing the email.{aiForm.includeImage ? ' Then gpt-image-1 will create the hero image.' : ''}</p>
                    </div>
                  )}

                  {/* ── PREVIEW step ── */}
                  {aiStep === 'preview' && aiResult && (
                    <div className="modal-body p-0">
                      <div className="row g-0" style={{ minHeight: 520 }}>

                        {/* Left: controls */}
                        <div className="col-12 col-lg-4 border-end p-4 d-flex flex-column gap-3">
                          <div>
                            <div className="badge bg-success mb-2">✓ Generated</div>
                            {aiResult.imageUrl && (
                              <div className="badge bg-info text-dark ms-1 mb-2">🖼 Image included</div>
                            )}
                            <p className="text-muted small mb-0">
                              Review the preview on the right. Edit the name below, then save as a reusable template.
                            </p>
                          </div>

                          <div>
                            <label className="form-label fw-semibold">Template Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={aiTemplateName}
                              onChange={e => setAiTemplateName(e.target.value)}
                              placeholder="Template name…"
                            />
                          </div>

                          {aiResult.imageUrl && (
                            <div>
                              <label className="form-label small text-muted">Hero Image URL</label>
                              <input
                                type="text"
                                className="form-control form-control-sm text-truncate"
                                value={aiResult.imageUrl}
                                readOnly
                                style={{ fontSize: '0.75rem' }}
                              />
                            </div>
                          )}

                          <div className="mt-auto d-flex flex-column gap-2">
                            <button
                              className="btn btn-success fw-semibold"
                              onClick={handleSaveAITemplate}
                              disabled={aiSaving}
                            >
                              {aiSaving ? '⏳ Saving…' : '💾 Save as Template'}
                            </button>
                            <button className="btn btn-outline-secondary btn-sm"
                              onClick={() => setAiStep('form')}>
                              ← Regenerate
                            </button>
                            <button className="btn btn-outline-secondary btn-sm"
                              onClick={() => {
                                setEmailCode(aiResult.html);
                                setEmailCodeType('html');
                                setEmailTemplateName(aiTemplateName);
                                setShowEmailForm(true);
                                setEditingTemplate(null);
                                setShowAIModal(false);
                                setAiStep('form');
                                setAiResult(null);
                                setActiveTab('emails');
                              }}>
                              ✏️ Open in Editor
                            </button>
                          </div>
                        </div>

                        {/* Right: live preview */}
                        <div className="col-12 col-lg-8" style={{ background: '#f4f6f9' }}>
                          <div className="d-flex align-items-center px-3 py-2 border-bottom bg-white" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            <span>📧 Email Preview</span>
                            <span className="ms-auto text-muted">Rendered in iframe — personalization placeholders shown as-is</span>
                          </div>
                          <div style={{ overflowX: 'auto', overflowY: 'auto', height: 500, background: '#f4f6f9' }}>
                            <iframe
                              srcDoc={aiResult.html}
                              title="Email Preview"
                              style={{ width: 640, minWidth: 640, height: 500, border: 'none', background: '#fff', display: 'block' }}
                              sandbox="allow-same-origin"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="modal-footer">
                    {aiStep === 'form' && (
                      <>
                        <button className="btn btn-outline-secondary"
                          onClick={() => { setShowAIModal(false); setAiResult(null); }}>
                          Cancel
                        </button>
                        <button
                          className="btn btn-success fw-semibold px-4"
                          onClick={handleGenerateAI}
                          disabled={!aiForm.topic.trim() || heroImageUploading}
                        >
                          {heroImageUploading ? '⏳ Uploading image…' : heroImageUrl ? '✨ Generate Email + Your Image' : `✨ Generate Email${aiForm.includeImage ? ' + Image' : ''}`}
                        </button>
                      </>
                    )}
                    {aiStep === 'preview' && (
                      <button className="btn btn-outline-secondary"
                        onClick={() => { setShowAIModal(false); setAiStep('form'); setAiResult(null); }}>
                        Close
                      </button>
                    )}
                  </div>

                </div>
              </div>
            </div>
          )}

          {showEmailForm && (
            <div className="card mb-4">
              <div className="card-body">
                <h5>{editingTemplate ? 'Edit Edit Email Template' : 'Create Email Template'}</h5>
                <div className="mb-3">
                  <label className="form-label">Template Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={emailTemplateName}
                    onChange={(e) => setEmailTemplateName(e.target.value)}
                    placeholder="e.g., Welcome Email, Newsletter January 2025"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Code Type</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        checked={emailCodeType === 'html'}
                        onChange={() => setEmailCodeType('html')}
                      />
                      <label className="form-check-label">HTML</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        checked={emailCodeType === 'json'}
                        onChange={() => setEmailCodeType('json')}
                      />
                      <label className="form-check-label">JSON (MJML)</label>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Template Code</label>
                  <div className="mb-2">
                    <button 
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => templateFileInputRef.current?.click()}
                    >
                      Select File
                    </button>
                    <input
                      ref={templateFileInputRef}
                      type="file"
                      accept=".html,.json"
                      style={{ display: 'none' }}
                      onChange={handleTemplateFileUpload}
                    />
                    <small className="text-muted ms-2">
                      Upload HTML or JSON file
                    </small>
                  </div>
                  <textarea
                    className="form-control font-monospace"
                    rows={15}
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                    placeholder={emailCodeType === 'html' 
                      ? '<html>\n  <body>\n    <h1>Hello {FIRST}!</h1>\n  </body>\n</html>'
                      : '{\n  "type": "mjml",\n  "content": "..."\n}'
                    }
                  />
                </div>

                <div className="d-flex gap-2">
                  <button 
                    type="button"
                    className="btn btn-info"
                    onClick={handlePreviewTemplate}
                    disabled={!emailCode || loading}
                  >
                    Preview Preview
                  </button>
                  <button 
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveEmailTemplate}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : editingTemplate ? 'Update Template' : 'Save Template'}
                  </button>
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEmailForm(false);
                      setEditingTemplate(null);
                      setEmailTemplateName('');
                      setEmailCode('');
                      setEmailCodeType('html');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="row g-3">
            {mktEmails.length === 0 ? (
              <div className="col-12">
                <div className="alert alert-info">
                  No email templates yet. Create your first template above!
                </div>
              </div>
            ) : (
              mktEmails.map(template => (
                <div key={template._id} className="col-md-6 col-lg-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h5 className="card-title">{template.mktemailname}</h5>
                      <p className="card-text text-muted">
                        <small>
                          Type: {template.mktemailtype || 'N/A'}<br />
                          ID: {template.mktemailid}<br />
                          Created: {new Date(template.mktemaildate).toLocaleDateString()}
                        </small>
                      </p>
                      {template.mktemailnote && (
                        <p className="card-text"><small>{template.mktemailnote}</small></p>
                      )}
                    </div>
                    <div className="card-footer">
                      <div className="d-flex gap-2 flex-wrap">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => loadEmailTemplate(template)}
                          title="Use in Campaign"
                        >
                          Use
                        </button>
                        <button 
                          className="btn btn-sm btn-info"
                          onClick={() => handleViewTemplate(template)}
                          title="Preview"
                        >
                          View
                        </button>
                        <button 
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEditTemplate(template)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteTemplate(template._id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Lists Tab */}
      {activeTab === 'lists' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Lists</h3>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowListForm(true);
                setEditingList(null);
                setListForm({ name: '', description: '' });
              }}
            >
              New List
            </button>
          </div>

          <p className="text-muted small">
            Custom-named, reusable groupings of contacts (e.g. "NY Architects Q3 push"). Add contacts to a list
            from the Contacts tab, then select one or more lists as recipients when sending a campaign.
          </p>

          {showListForm && (
            <div className="card mb-4">
              <div className="card-body">
                <h5>{editingList ? 'Edit List' : 'Create New List'}</h5>
                <form onSubmit={handleListFormSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={listForm.name}
                        onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={listForm.description}
                        onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button type="submit" className="btn btn-primary me-2">
                      {editingList ? 'Update List' : 'Create List'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => { setShowListForm(false); setEditingList(null); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Contacts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lists.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No lists yet. Create one, then add contacts to it from the Contacts tab.
                    </td>
                  </tr>
                ) : (
                  lists.map(list => (
                    <React.Fragment key={list._id}>
                      <tr>
                        <td className="fw-semibold">{list.name}</td>
                        <td className="text-muted">{list.description || '—'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={() => handleToggleListMembers(list)}
                          >
                            <span className="badge bg-primary">{list.contactCount ?? 0}</span>
                            {' '}{expandedListId === list._id ? '▲ Hide' : '▼ View'}
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => {
                              setEditingList(list);
                              setListForm({ name: list.name, description: list.description || '' });
                              setShowListForm(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteList(list)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      {expandedListId === list._id && (
                        <tr>
                          <td colSpan={4} className="p-0">
                            <div className="p-3" style={{ background: '#f9fafb' }}>
                              {expandedListLoading ? (
                                <div className="text-muted small">Loading contacts…</div>
                              ) : expandedListContacts.length === 0 ? (
                                <div className="text-muted small">No contacts in this list.</div>
                              ) : (
                                <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                                  <table className="table table-sm table-borderless mb-0">
                                    <thead>
                                      <tr className="text-muted small">
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Company</th>
                                        <th>Profession</th>
                                        <th>Memo</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedListContacts.map(contact => (
                                        <tr key={contact._id}>
                                          <td className="small">{contact.FIRST} {contact.LAST}</td>
                                          <td className="small">{contact.EMAIL}</td>
                                          <td className="small">{contact.COMPANY}</td>
                                          <td className="small">
                                            <span className="badge bg-primary" title={getContactProfession(contact, professions).name}>
                                              {getContactProfession(contact, professions).code}
                                            </span>
                                          </td>
                                          <td className="small text-muted" style={{ maxWidth: 240 }} title={contact.NOTE}>
                                            {contact.NOTE ? (contact.NOTE.length > 60 ? contact.NOTE.slice(0, 60) + '…' : contact.NOTE) : '—'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unsubscribers Modal */}
      {unsubModal.open && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Unsubscribers — {unsubModal.campaignName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setUnsubModal(m => ({ ...m, open: false }))}
                />
              </div>
              <div className="modal-body">
                {unsubModal.loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-secondary me-2" />
                    Loading…
                  </div>
                ) : unsubModal.contacts.length === 0 ? (
                  <p className="text-muted mb-0">No unsubscribers recorded after this campaign was sent.</p>
                ) : (
                  <>
                    <p className="text-muted small mb-3">
                      {unsubModal.contacts.length} contact{unsubModal.contacts.length !== 1 ? 's' : ''} unsubscribed on or after this campaign was sent.
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Unsubscribed</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unsubModal.contacts.map((c: any, i: number) => (
                            <tr key={i}>
                              <td>{[c.FIRST, c.LAST].filter(Boolean).join(' ') || '—'}</td>
                              <td><span className="text-monospace small">{c.EMAIL}</span></td>
                              <td className="small text-nowrap">
                                {c.EMAILOPTOUTDATE
                                  ? new Date(c.EMAILOPTOUTDATE).toLocaleDateString('en-US', { timeZone: 'America/New_York' })
                                  : '—'}
                              </td>
                              <td className="small text-muted">{c.EMAILOPTOUTREASON || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setUnsubModal(m => ({ ...m, open: false }))}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Results Modal */}
      {resultsModal.open && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Results — {resultsModal.campaignName}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setResultsModal(m => ({ ...m, open: false }))}
                />
              </div>
              <div className="modal-body">
                {resultsModal.loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-secondary me-2" />
                    Loading…
                  </div>
                ) : !resultsModal.data ? (
                  <p className="text-muted mb-0">No send results found for this campaign.</p>
                ) : (
                  <>
                    <div className="row g-3 mb-3 text-center">
                      <div className="col-4">
                        <div className="border rounded p-2">
                          <div className="fs-4 fw-bold">{resultsModal.data.campaign.totalRecipients}</div>
                          <div className="text-muted small">Targeted</div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="border rounded p-2 border-success">
                          <div className="fs-4 fw-bold text-success">{resultsModal.data.current.sent || 0}</div>
                          <div className="text-muted small">Delivered</div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="border rounded p-2 border-danger">
                          <div className="fs-4 fw-bold text-danger">{resultsModal.data.current.failed || 0}</div>
                          <div className="text-muted small">Still Failed</div>
                        </div>
                      </div>
                    </div>

                    {resultsModal.data.totals.failed && resultsModal.data.totals.sent !== undefined && (
                      <p className="text-muted small mb-3">
                        {resultsModal.data.totals.sent + (resultsModal.data.totals.failed || 0)} total send attempts logged
                        (retries count separately) — figures above are deduped to each contact's latest attempt.
                      </p>
                    )}

                    {(resultsModal.data.current.failed || 0) > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-danger small">
                          {resultsModal.data.current.failed} recipient{resultsModal.data.current.failed !== 1 ? 's' : ''} never received this email.
                        </span>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={handleRetryFailed}
                          disabled={resultsModal.retrying}
                        >
                          {resultsModal.retrying ? (
                            <><span className="spinner-border spinner-border-sm me-1" />Retrying…</>
                          ) : (
                            `Retry Failed (${resultsModal.data.current.failed})`
                          )}
                        </button>
                      </div>
                    )}

                    {resultsModal.data.failedContacts.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Email</th>
                              <th>Last Attempt</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultsModal.data.failedContacts.map((c) => (
                              <tr key={c.PID}>
                                <td><span className="text-monospace small">{c.emailto}</span></td>
                                <td className="small text-nowrap">
                                  {new Date(c.senttimestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                                </td>
                                <td className="small text-muted text-truncate" style={{ maxWidth: 320 }} title={c.errorMessage}>
                                  {c.errorMessage || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setResultsModal(m => ({ ...m, open: false }))}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Schedule/Send Campaign</h5>
                <button className="btn-close" onClick={() => setShowScheduleModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Choose when to send this campaign:</p>
                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-success" onClick={handleScheduledSend} disabled={loading}>
                  {loading ? '○ Sending...' : 'Send Send Now/Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Test Send Test Email</h5>
                <button className="btn-close" onClick={() => setShowTestEmailModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Enter an email address to receive a test:</p>
                <input
                  type="email"
                  className="form-control"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowTestEmailModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleTestEmail} disabled={loading}>
                  {loading ? '○ Sending...' : ' Send Test'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email History Modal */}
      {showEmailHistoryModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  📧 Email History - {emailHistory?.contact.FIRST} {emailHistory?.contact.LAST}
                </h5>
                <button className="btn-close" onClick={() => {
                  setShowEmailHistoryModal(false);
                  setEmailHistory(null);
                }}></button>
              </div>
              <div className="modal-body">
                {loadingHistory ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading email history...</p>
                  </div>
                ) : emailHistory ? (
                  <>
                    {/* Contact Info */}
                    <div className="mb-3 p-3 bg-light rounded">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Contact:</strong> {emailHistory.contact.FIRST} {emailHistory.contact.LAST}
                        </div>
                        <div className="col-md-6">
                          <strong>Email:</strong> {emailHistory.contact.EMAIL}
                        </div>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="row mb-4">
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h3 className="mb-0">{emailHistory.totalSent}</h3>
                            <small className="text-muted">Total Sent</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h3 className="mb-0 text-success">{emailHistory.stats.sent || 0}</h3>
                            <small className="text-muted">Delivered</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h3 className="mb-0 text-danger">{emailHistory.stats.failed || 0}</h3>
                            <small className="text-muted">Failed</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h3 className="mb-0 text-info">{emailHistory.stats.opened || 0}</h3>
                            <small className="text-muted">Opened</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email History Table */}
                    <div className="table-responsive">
                      {emailHistory.logs.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <p>No emails sent to this contact yet.</p>
                        </div>
                      ) : (
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Date & Time</th>
                              <th>Campaign</th>
                              <th>Subject</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailHistory.logs.map(log => (
                              <tr key={log._id}>
                                <td>
                                  {new Date(log.senttimestamp).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}<br />
                                  <small className="text-muted">
                                    {new Date(log.senttimestamp).toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}
                                  </small>
                                </td>
                                <td>
                                  {log.campaignid ? (
                                    <span className="badge bg-primary">
                                      Campaign #{log.campaignid}
                                    </span>
                                  ) : (
                                    <span className="badge bg-secondary">Direct Email</span>
                                  )}
                                </td>
                                <td>
                                  <div style={{ maxWidth: '300px' }}>
                                    {log.emailsubject}
                                  </div>
                                </td>
                                <td>
                                  {log.status === 'sent' && (
                                    <span className="badge bg-success">✓ Sent</span>
                                  )}
                                  {log.status === 'failed' && (
                                    <span className="badge bg-danger" title={log.errorMessage}>
                                      ✗ Failed
                                    </span>
                                  )}
                                  {log.status === 'opened' && (
                                    <span className="badge bg-info">👁️ Opened</span>
                                  )}
                                  {log.status === 'clicked' && (
                                    <span className="badge bg-warning">🖱️ Clicked</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <p>Failed to load email history.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => {
                  setShowEmailHistoryModal(false);
                  setEmailHistory(null);
                }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Preview Email</h5>
                <button className="btn-close" onClick={() => setShowPreviewModal(false)}></button>
              </div>
              <div className="modal-body p-0">
                <div style={{ overflowX: 'auto', background: '#f4f6f9' }}>
                  <iframe
                    key={previewKey}
                    srcDoc={previewHtml}
                    title="Email Preview"
                    style={{ width: 640, minWidth: 640, height: 560, border: 'none', background: '#fff', display: 'block', margin: '0 auto' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
              <div className="modal-footer flex-column align-items-stretch gap-2">
                {/* Test recipients send bar */}
                <div className="d-flex align-items-center gap-2 w-100 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                  <span className="small text-muted fw-semibold" style={{ whiteSpace: 'nowrap' }}>Send test to:</span>
                  <div className="d-flex flex-wrap gap-1 flex-grow-1">
                    {testRecipients.length === 0 ? (
                      <span className="text-muted small fst-italic">No recipients — click "Test Recipients" to add</span>
                    ) : (
                      testRecipients.map(email => (
                        <span key={email} className="badge bg-secondary" style={{ fontWeight: 400 }}>{email}</span>
                      ))
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-success"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={handleSendTestToAll}
                    disabled={sendingTestToAll || testRecipients.length === 0}
                  >
                    {sendingTestToAll ? 'Sending…' : `Send Test (${testRecipients.length})`}
                  </button>
                </div>
                <div className="d-flex justify-content-between w-100">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowTestRecipientsPanel(p => !p)}
                  >
                    Edit Test Recipients
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCSVImportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Contacts from CSV</h5>
                <button className="btn-close" onClick={resetCSVImport}></button>
              </div>
              <div className="modal-body">
                {/* Step Indicator */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between">
                    <div className={`text-center ${csvImportStep >= 1 ? 'text-primary fw-bold' : 'text-muted'}`}>
                      1. Template
                    </div>
                    <div className={`text-center ${csvImportStep >= 2 ? 'text-primary fw-bold' : 'text-muted'}`}>
                      2. Preview & Map
                    </div>
                    <div className={`text-center ${csvImportStep >= 3 ? 'text-primary fw-bold' : 'text-muted'}`}>
                      3. Validate
                    </div>
                    <div className={`text-center ${csvImportStep >= 4 ? 'text-primary fw-bold' : 'text-muted'}`}>
                      4. Results
                    </div>
                  </div>
                  <div className="progress mt-2" style={{ height: '4px' }}>
                    <div 
                      className="progress-bar" 
                      style={{ width: `${(csvImportStep / 4) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Step 1: Template & Instructions */}
                {csvImportStep === 1 && (
                  <div>
                    <h6 className="mb-3">Download Template & Prepare Your Data</h6>
                    
                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Template</h6>
                        <p className="card-text">Download our CSV template with the correct format:</p>
                        <button className="btn btn-primary btn-sm" onClick={handleDownloadTemplate}>
                          Download Template
                        </button>
                      </div>
                    </div>

                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Required Fields</h6>
                        <ul className="mb-0">
                          <li><strong>EMAIL</strong> - Email address (required, must be valid and unique)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Optional Fields</h6>
                        <p className="text-muted small mb-2">Only <strong>EMAIL</strong> is required. All other fields are optional.</p>
                        <div className="row">
                          <div className="col-md-6">
                            <strong className="small text-uppercase text-muted">Identity</strong>
                            <ul className="mb-2 small">
                              <li><strong>FIRST</strong> / <strong>LAST</strong> — First and last name</li>
                              <li><strong>COMPANY</strong> — Company / place name</li>
                              <li><strong>TITLE</strong> — Job title</li>
                              <li><strong>PROFESSIONID</strong> — Canonical profession code (ARCH, PE, LAW, DDS…)</li>
                              <li><strong>NOTE</strong> — Memo / internal note</li>
                              <li><strong>RELATION</strong> / <strong>PROFESSION</strong> — Legacy free-text fields; still accepted and auto-mapped to a canonical code if PROFESSIONID isn't provided</li>
                            </ul>
                            <strong className="small text-uppercase text-muted">Address</strong>
                            <ul className="mb-2 small">
                              <li><strong>STREET</strong> — Street address</li>
                              <li><strong>CITY</strong> — City</li>
                              <li><strong>STATE</strong> — 2-letter state code</li>
                              <li><strong>ZIP</strong> — ZIP code (9-digit ZIP+4 trimmed to 5)</li>
                            </ul>
                            <strong className="small text-uppercase text-muted">Contact</strong>
                            <ul className="mb-0 small">
                              <li><strong>TEL_BUS</strong> — Business phone</li>
                              <li><strong>WEB</strong> — Website URL</li>
                            </ul>
                          </div>
                          <div className="col-md-6">
                            <strong className="small text-uppercase text-muted">Provider / Licensure</strong>
                            <ul className="mb-2 small">
                              <li><strong>NPI</strong> — National Provider Identifier</li>
                              <li><strong>CREDENTIAL</strong> — Degree / credential (DDS, DMD…)</li>
                              <li><strong>GENDER</strong> — Gender</li>
                              <li><strong>SPECIALTY</strong> — Specialty or practice type</li>
                              <li><strong>LICENSE_NUMBER</strong> — State license number</li>
                              <li><strong>LICENSE_STATE</strong> — Issuing state (2 letters)</li>
                            </ul>
                            <strong className="small text-uppercase text-muted">Google Places</strong>
                            <ul className="mb-0 small">
                              <li><strong>GOOGLE_RATING</strong> — Star rating (numeric)</li>
                              <li><strong>GOOGLE_REVIEW_COUNT</strong> — Number of reviews</li>
                              <li><strong>GOOGLE_ADDRESS</strong> — Formatted address from Google</li>
                              <li><strong>PLACE_ID</strong> — Google Place ID</li>
                            </ul>
                          </div>
                        </div>
                        <div className="mt-2 p-2 bg-light rounded small">
                          <strong>Auto-map:</strong> if PROFESSIONID isn't mapped, legacy RELATION/PROFESSION text is
                          converted automatically where recognized — ARC/RA→ARCH, ENG→PE, DDS/DMD→DDS, LAW→LAW, etc.
                          Unrecognized values fall back to the Default Profession set below.
                        </div>
                      </div>
                    </div>

                    <div className="card mb-3 bg-light">
                      <div className="card-body">
                        <h6 className="card-title">Guidelines</h6>
                        <ul className="mb-0">
                          <li>File must be in CSV format</li>
                          <li>First row should contain column headers</li>
                          <li><strong>Only EMAIL is required</strong> - all other fields are optional</li>
                          <li>Email addresses must be unique (no duplicates)</li>
                          <li>If provided, state codes should be 2 letters (e.g., CA, NY, TX)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-4">
                      <h6>Upload Your CSV File</h6>
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={handleCSVFileSelect}
                        disabled={loading}
                      />
                      {loading && <div className="mt-2 text-muted">Reading file...</div>}
                    </div>
                  </div>
                )}

                {/* Step 2: Preview & Field Mapping */}
                {csvImportStep === 2 && (
                  <div>
                    <h6 className="mb-3">Preview Data & Map Fields</h6>
                    <p className="text-muted">Found {csvData.length} rows. Map CSV columns to contact fields:</p>
                    
                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Field Mapping</h6>
                        <div className="row g-2">
                          {csvHeaders.map(csvHeader => (
                            <div key={csvHeader} className="col-md-6">
                              <label className="form-label small">{csvHeader}</label>
                              <select
                                className="form-select form-select-sm"
                                value={fieldMapping[csvHeader] || ''}
                                onChange={(e) => setFieldMapping({ ...fieldMapping, [csvHeader]: e.target.value })}
                              >
                                <option value="">-- Skip this column --</option>
                                <optgroup label="Identity">
                                  <option value="FIRST">First Name</option>
                                  <option value="LAST">Last Name</option>
                                  <option value="EMAIL">Email *</option>
                                  <option value="COMPANY">Company</option>
                                  <option value="TITLE">Title</option>
                                  <option value="PROFESSIONID">Profession (canonical code, e.g. ARCH, PE)</option>
                                  <option value="RELATION">Relation Code (legacy)</option>
                                  <option value="PROFESSION">Profession (legacy free text)</option>
                                  <option value="NOTE">Memo</option>
                                </optgroup>
                                <optgroup label="Address">
                                  <option value="STREET">Street Address</option>
                                  <option value="CITY">City</option>
                                  <option value="STATE">State</option>
                                  <option value="ZIP">ZIP Code</option>
                                </optgroup>
                                <optgroup label="Contact">
                                  <option value="TEL_BUS">Business Phone</option>
                                  <option value="WEB">Website</option>
                                </optgroup>
                                <optgroup label="Provider / Licensure">
                                  <option value="NPI">NPI</option>
                                  <option value="CREDENTIAL">Credential (DDS, DMD…)</option>
                                  <option value="GENDER">Gender</option>
                                  <option value="SPECIALTY">Specialty</option>
                                  <option value="LICENSE_NUMBER">License Number</option>
                                  <option value="LICENSE_STATE">License State</option>
                                </optgroup>
                                <optgroup label="Google Places">
                                  <option value="GOOGLE_RATING">Google Rating</option>
                                  <option value="GOOGLE_REVIEW_COUNT">Google Review Count</option>
                                  <option value="GOOGLE_ADDRESS">Google Address</option>
                                  <option value="PLACE_ID">Place ID</option>
                                </optgroup>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Data Preview (First 10 rows)</h6>
                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className="table table-sm table-bordered">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th>#</th>
                                {csvHeaders.map(h => (
                                  <th key={h}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {csvData.slice(0, 10).map((row, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  {csvHeaders.map(h => (
                                    <td key={h}>{row[h]}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button className="btn btn-secondary" onClick={() => setCsvImportStep(1)}>
                        Back
                      </button>
                      <button className="btn btn-primary" onClick={validateCSVData}>
                        Next: Validate
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Validation & Options */}
                {csvImportStep === 3 && (
                  <div>
                    <h6 className="mb-3">Validation Results & Import Options</h6>
                    
                    {validationErrors.length > 0 && (
                      <div className="alert alert-warning">
                        <h6 className="alert-heading">Found {validationErrors.length} validation errors</h6>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>Field</th>
                                <th>Error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validationErrors.slice(0, 20).map((err, idx) => (
                                <tr key={idx}>
                                  <td>{err.row}</td>
                                  <td>{err.field}</td>
                                  <td>{err.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {validationErrors.length > 20 && (
                            <p className="text-muted small mt-2 mb-0">
                              Showing first 20 of {validationErrors.length} errors
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {validationErrors.length === 0 && (
                      <div className="alert alert-success">
                        All rows passed validation! Ready to import {csvData.length} contacts.
                      </div>
                    )}

                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Import Options</h6>
                        
                        <div className="form-check mb-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={importOptions.skipDuplicates}
                            onChange={(e) => setImportOptions({ ...importOptions, skipDuplicates: e.target.checked })}
                            disabled={importOptions.updateExisting}
                          />
                          <label className="form-check-label">
                            Skip duplicate emails (don't import contacts with existing email addresses)
                          </label>
                        </div>

                        <div className="form-check mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={importOptions.updateExisting}
                            onChange={(e) => setImportOptions({ 
                              ...importOptions, 
                              updateExisting: e.target.checked,
                              skipDuplicates: !e.target.checked 
                            })}
                          />
                          <label className="form-check-label">
                            Update existing contacts (overwrite data for contacts with matching emails)
                          </label>
                        </div>

                        <div className="mb-2">
                          <label className="form-label">Default Profession (for contacts without profession specified)</label>
                          <select
                            className="form-select"
                            value={importOptions.defaultProfessionID}
                            onChange={(e) => setImportOptions({ ...importOptions, defaultProfessionID: e.target.value })}
                          >
                            {professions.length === 0 ? (
                              <option value="ARCH">Architect</option>
                            ) : (
                              professions.map(prof => (
                                <option key={prof.professionID} value={prof.professionID}>
                                  {prof.name} ({prof.professionID})
                                </option>
                              ))
                            )}
                          </select>
                          <small className="text-muted">
                            This profession will be applied to contacts that don't have a profession specified in the CSV.
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button className="btn btn-secondary" onClick={() => setCsvImportStep(2)}>
                        Back
                      </button>
                      <button 
                        className="btn btn-success" 
                        onClick={executeCSVImport}
                        disabled={loading}
                      >
                        {loading ? 'Importing...' : `Import ${csvData.length - validationErrors.filter((e, i, arr) => arr.findIndex(x => x.row === e.row) === i).length} Contacts`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Results */}
                {csvImportStep === 4 && (
                  <div>
                    <h6 className="mb-3">Import Complete</h6>
                    
                    <div className="row g-3 mb-3">
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h3 className="mb-0">{importResults.total}</h3>
                            <small className="text-muted">Total Rows</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center border-success">
                          <div className="card-body">
                            <h3 className="mb-0 text-success">{importResults.success}</h3>
                            <small className="text-muted">Imported</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center border-warning">
                          <div className="card-body">
                            <h3 className="mb-0 text-warning">{importResults.skipped}</h3>
                            <small className="text-muted">Skipped</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center border-danger">
                          <div className="card-body">
                            <h3 className="mb-0 text-danger">{importResults.failed}</h3>
                            <small className="text-muted">Failed</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {importResults.errors.length > 0 && (
                      <div className="card mb-3">
                        <div className="card-body">
                          <h6 className="card-title text-danger">Errors</h6>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Row</th>
                                  <th>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {importResults.errors.map((err, idx) => (
                                  <tr key={idx}>
                                    <td>{err.row}</td>
                                    <td>{err.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    <button className="btn btn-primary" onClick={resetCSVImport}>
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingEmailAdmin;
